/**
 * KIMCAO Nail Builder - Shopify Theme Integration
 * Custom Nail Design Builder Component
 *
 * This script initializes and manages the nail builder application
 * within a Shopify theme context.
 */

(function() {
    'use strict';

    // ============================================
    // CONSTANTS & CONFIG
    // ============================================

    const FINGERS = ['thumb', 'index', 'middle', 'ring', 'pinky'];
    const SHAPES = ['almond', 'rectangle', 'stiletto', 'round'];

    const COLOR_PRESETS = [
        '#f0ede8', '#e8dfd8', '#f5f0eb', '#fad0c4', '#ffecd2',
        '#fcb69f', '#fed6e3', '#d299c2', '#c8b8db', '#a89dc7',
        '#3498db', '#1abc9c', '#2ecc71', '#f1c40f', '#e67e22',
        '#000000', '#333333', '#666666', '#999999', '#ffffff'
    ];

    const NUDE_PRESETS = [
        { id: 'nude1', color: '#f5d5c8', name: 'Light Nude' },
        { id: 'nude2', color: '#e8c4b8', name: 'Soft Nude' },
        { id: 'nude3', color: '#d4a894', name: 'Medium Nude' },
        { id: 'nude4', color: '#c99a82', name: 'Warm Nude' },
        { id: 'nude5', color: '#b8806a', name: 'Deep Nude' },
        { id: 'nude6', color: '#ffeee6', name: 'Sheer Pink' }
    ];

    const CHARM_TYPES = [
        { id: 'jellyOrb1', name: 'Jelly Orb S', type: 'jelly' },
        { id: 'jellyOrb2', name: 'Jelly Orb M', type: 'jelly' },
        { id: 'jellyOrb3', name: 'Jelly Orb L', type: 'jelly' },
        { id: 'metalOrb1', name: 'Metal Orb S', type: 'metal' },
        { id: 'metalOrb2', name: 'Metal Orb M', type: 'metal' },
        { id: 'metalOrb3', name: 'Metal Orb L', type: 'metal' },
        { id: 'kc3dshape1', name: '3D Shape', type: 'custom' }
    ];

    const MAX_CHARMS_PER_NAIL = 6;
    const MIN_CHARM_SCALE = 0.1;
    const MAX_CHARM_SCALE = 0.4;
    const MAX_CHARM_SCALE_3D = 1.2;

    // Nail shape to file mapping
    const NAIL_FILES = {
        almond: {
            thumb: 'KC_almondthumb.svg',
            index: 'KC_almondindex.svg',
            middle: 'KC_almondmiddle.svg',
            ring: 'KC_almondring.svg',
            pinky: 'KC_almondpinky.svg'
        },
        rectangle: {
            thumb: 'KC_rectanglethumb.svg',
            index: 'KC_rectangleindex.svg',
            middle: 'KC_rectanglemiddle.svg',
            ring: 'KC_rectanglering.svg',
            pinky: 'KC_rectanglepinky.svg'
        },
        stiletto: {
            thumb: 'KC_stillettothumb.svg',
            index: 'KC_stillettoindex.svg',
            middle: 'KC_stillettomiddle.svg',
            ring: 'KC_stillettoring.svg',
            pinky: 'KC_stilettopinky.svg'
        },
        round: {
            thumb: 'KC_roundthumb.svg',
            index: 'KC_roundpinky-14.svg',
            middle: 'KC_roundmiddle.svg',
            ring: 'KC_roundring.svg',
            pinky: 'KC_roundpinky-11.svg'
        }
    };

    const LEGACY_PATTERN_DEFS = [
        { id: 'basedesign1', file: 'KC_basedesign1.svg', label: 'Pattern 1', source: 'legacy' },
        { id: 'basedesign2', file: 'KC_basedesign2.svg', label: 'Pattern 2', source: 'legacy' },
        { id: 'basedesign3', file: 'KC_basedesign3.svg', label: 'Pattern 3', source: 'legacy' }
    ];

    const FRENCH_TIP_PATTERN_DEF = { id: 'frenchtip', file: null, label: 'French tip', procedural: true };

    const LEGACY_PATTERN_FILES = LEGACY_PATTERN_DEFS.reduce((acc, pattern) => {
        if (pattern.file) acc[pattern.id] = pattern.file;
        return acc;
    }, {});

    const LEGACY_PATTERN_MAP = LEGACY_PATTERN_DEFS.reduce((acc, pattern) => {
        acc[pattern.id] = pattern;
        return acc;
    }, {});

    const PATTERN_PRELOAD_LIMIT_DESKTOP = 24;
    const PATTERN_PRELOAD_LIMIT_MOBILE = 8;
    const PATTERN_DEFERRED_PRELOAD_BATCH = 10;

    function parseJSONSafely(value, fallback = null) {
        if (value === null || value === undefined) return fallback;
        if (typeof value === 'object') return value;
        if (typeof value !== 'string') return fallback;

        try {
            return JSON.parse(value);
        } catch (_error) {
            return fallback;
        }
    }

    function toTextArray(value) {
        if (Array.isArray(value)) {
            return value
                .map((item) => String(item || '').trim())
                .filter(Boolean);
        }

        if (typeof value === 'string') {
            const parsed = parseJSONSafely(value, null);
            if (Array.isArray(parsed)) {
                return parsed
                    .map((item) => String(item || '').trim())
                    .filter(Boolean);
            }

            return value
                .split(',')
                .map((item) => item.trim())
                .filter(Boolean);
        }

        return [];
    }

    function normalizeDynamicPattern(rawPattern) {
        if (!rawPattern || typeof rawPattern !== 'object') return null;

        const id = String(rawPattern.id || rawPattern.handle || rawPattern.slug || '').trim();
        if (!id) return null;

        const label = String(rawPattern.title || rawPattern.label || id).trim();
        const status = String(rawPattern.status || 'published').toLowerCase();
        if (status !== 'published') return null;

        const exportMode = rawPattern.export_mode || rawPattern.exportMode || 'universal';
        const svgUniversal = rawPattern.svg_universal || rawPattern.svgUniversal || '';
        const svgMapRaw = rawPattern.svg_map_json || rawPattern.svgMapJson || rawPattern.svg_map || rawPattern.svgMap;
        const svgMap = parseJSONSafely(svgMapRaw, svgMapRaw && typeof svgMapRaw === 'object' ? svgMapRaw : null);

        return {
            id,
            label,
            source: 'metaobject',
            exportMode,
            styleTags: toTextArray(rawPattern.style_tags || rawPattern.styleTags),
            previewImage: rawPattern.preview_image || rawPattern.previewImage || '',
            svgUniversal: typeof svgUniversal === 'string' ? svgUniversal : '',
            svgMap: svgMap && typeof svgMap === 'object' ? svgMap : null
        };
    }

    const FRENCH_TIP_MIN_DEPTH = 6;
    const FRENCH_TIP_MAX_DEPTH = 45;
    const FRENCH_TIP_DEFAULT_DEPTH = 18;
    const FRENCH_TIP_MIN_CURVATURE = -100;
    const FRENCH_TIP_MAX_CURVATURE = 100;
    const FRENCH_TIP_DEFAULT_CURVATURE = 35;

    // Default nail placements on hand
    const DEFAULT_PLACEMENTS = {
        almond: {
            thumb: { x: 1269, y: 880.92, scale: 1.35, rotation: 3.2 },
            index: { x: 889.95, y: -46.66, scale: 1.24, rotation: 0 },
            middle: { x: 588.38, y: -157.31, scale: 1.49, rotation: 5 },
            ring: { x: 233.83, y: -68.79, scale: 1.46, rotation: 7 },
            pinky: { x: -35.02, y: 348.93, scale: 1.32, rotation: 0 }
        },
        rectangle: {
            thumb: { x: 1269, y: 880.92, scale: 1.35, rotation: 3.2 },
            index: { x: 889.95, y: -46.66, scale: 1.24, rotation: 0 },
            middle: { x: 588.38, y: -157.31, scale: 1.49, rotation: 5 },
            ring: { x: 233.83, y: -68.79, scale: 1.46, rotation: 7 },
            pinky: { x: -35.02, y: 348.93, scale: 1.32, rotation: 0 }
        },
        stiletto: {
            thumb: { x: 1269, y: 880.92, scale: 1.35, rotation: 3.2 },
            index: { x: 889.95, y: -46.66, scale: 1.24, rotation: 0 },
            middle: { x: 588.38, y: -157.31, scale: 1.49, rotation: 5 },
            ring: { x: 233.83, y: -68.79, scale: 1.46, rotation: 7 },
            pinky: { x: -35.02, y: 348.93, scale: 1.32, rotation: 0 }
        },
        round: {
            thumb: { x: 1269, y: 880.92, scale: 1.35, rotation: 3.2 },
            index: { x: 889.95, y: -46.66, scale: 1.24, rotation: 0 },
            middle: { x: 588.38, y: -157.31, scale: 1.49, rotation: 5 },
            ring: { x: 233.83, y: -68.79, scale: 1.46, rotation: 7 },
            pinky: { x: -35.02, y: 348.93, scale: 1.32, rotation: 0 }
        }
    };

    // Thumb coordinate transformations
    const THUMB_COORD_TRANSFORMS = {
        almond: { scaleX: 0.669, scaleY: 0.830, perspectiveStrength: 0.15 },
        round: { scaleX: 0.826, scaleY: 0.986, perspectiveStrength: 0.12 },
        rectangle: { scaleX: 0.85, scaleY: 0.95, perspectiveStrength: 0.10 },
        stiletto: { scaleX: 0.80, scaleY: 0.90, perspectiveStrength: 0.18 }
    };

    // 3D Shape path data
    const KC_3DSHAPE_PATH1 = "M43.05,167.45c-5.94-6.72-10.5-14.86-17.88-20.08-10.08-7.12-21.2-4.6-23.18,8.39-3.98,30.36,38.52,34.72,44.83,67.76,1.41,5.3,2.98,13.57,8.14,15.65,6.15,1.98,8.72-6.21,8.02-11.1-.36-4.31-1.66-8.52-2.82-12.68-1.9-6.35-3.33-12.92-5.08-19.41-2.42-9.73-5.02-18.99-10.68-27.17";
    const KC_3DSHAPE_PATH2 = "M83.46,176.88c.03,15.26.81,30.61.15,45.88-.1,5.03-1.56,10.56-.84,15.43,2.75,8.29,10.96-6.87,12.48-9.82,11.7-18.63,18.13-38.78,22.74-60.17,3.84-12.03,8.03-23.76,10.61-36.11,3.38-12.42,6.2-25.07,6.42-37.99-.05-12.58,2.4-25.13,1.09-37.7-2.33-17.35-10.97-37.18-23.93-49.19-15.41-12.25-25.38-1.93-26.76,15.13-2.49,16.78,11.55,29.24,16.98,43.96,3.99,9.83,8.28,19.75,9.91,30.3,1.21,12.51,2.78,25.04,2.21,37.59-.95,12.49-5.61,24.25-8.52,36.37-2.2,8.82-4.54,17.63-7.35,26.21-.91,2.62-3.69,11.98-2.57,4.07,3-19.69,3.44-40.2.18-59.92-1.66-8.73-4.37-17.23-6.05-25.95-2.73-13.36-7.05-26.66-14.32-38.34-5.08-8.24-10.09-16.55-17.47-22.81-13.13-11.83-38.37-18.29-47.51,1.35-4.35,10.22-6.3,23.24-2.11,33.77,3.53,8.63,11.38,14.29,18.54,19.81,11.44,9.28,23.96,16.6,35.3,26.08,11.7,10.14,19.79,25.15,20.83,40.69";
    const KC_3DSHAPE_VB_W = 140.19;
    const KC_3DSHAPE_VB_H = 240.64;
    const HAND_BASE_FILL = '#e8e5e0';
    const HAND_VIEWBOX = { width: 1173.64, height: 2223.77 };
    const HAND_LAYOUT_FIT = { widthRatio: 0.8, heightRatio: 0.9 };
    const HAND_NAIL_CANVAS_SIZE = { width: 300, height: 540 };
    const FINGER_LAYER_ORDER = {
        thumb: 5,
        index: 4,
        middle: 3,
        ring: 2,
        pinky: 1
    };

    // ============================================
    // NAIL BUILDER CLASS
    // ============================================

    class KimcaoNailBuilder {
        constructor(container, options = {}) {
            this.container = container;
            const defaultShape = SHAPES.includes(options.defaultShape) ? options.defaultShape : 'almond';
            const defaultHand = options.defaultHand === 'right' ? 'right' : 'left';
            this.options = {
                assetBasePath: options.assetBasePath || '',
                productId: options.productId || null,
                variantId: options.variantId || null,
                defaultShape,
                defaultHand,
                patternCatalog: options.patternCatalog || null,
                includeLegacyPatterns: options.includeLegacyPatterns !== false,
                onDesignChange: options.onDesignChange || null,
                ...options
            };

            // State
            this.state = {
                hand: this.options.defaultHand,
                hands: {
                    left: this.createDefaultHandState(this.options.defaultShape),
                    right: this.createDefaultHandState(this.options.defaultShape)
                }
            };

            Object.defineProperties(this.state, {
                shapeSet: {
                    get: () => this.state.hands[this.state.hand].shapeSet,
                    set: (value) => {
                        if (SHAPES.includes(value)) {
                            this.state.hands[this.state.hand].shapeSet = value;
                        }
                    }
                },
                activeFinger: {
                    get: () => this.state.hands[this.state.hand].activeFinger,
                    set: (value) => {
                        if (FINGERS.includes(value)) {
                            this.state.hands[this.state.hand].activeFinger = value;
                        }
                    }
                },
                nails: {
                    get: () => this.state.hands[this.state.hand].nails,
                    set: (value) => {
                        this.state.hands[this.state.hand].nails = value && typeof value === 'object' ? value : {};
                    }
                }
            });

            // Editor state
            this.editorState = {
                selectedCharm: null,
                isDragging: false,
                isResizing: false,
                activePointerId: null,
                dragOffset: { x: 0, y: 0 },
                gradientHandles: {
                    start: { x: 0.5, y: 0 },
                    end: { x: 0.5, y: 1 },
                    mid: { x: 0.5, y: 0.5 }
                },
                draggingHandle: null
            };

            // SVG cache
            this.svgCache = {};
            this.svgPathCache = {};
            this.frenchTipContourCache = {};
            this.failedAssetLoads = new Set();
            this.handOverlayRefreshFrame = null;
            this.handOverlayRefreshTimers = [];
            this.handOverlayResizeObserver = null;
            this.handOverlayRenderId = 0;
            this.debugMode = new URLSearchParams(window.location.search).get('dev') === '1';

            // Pattern registry (dynamic metaobjects + legacy fallback)
            const resolvedCatalog = this.options.patternCatalog
                || (window.KIMCAO_PATTERN_CATALOG && typeof window.KIMCAO_PATTERN_CATALOG === 'object'
                    ? window.KIMCAO_PATTERN_CATALOG
                    : { patterns: [] });
            this.patternRegistry = this.createPatternRegistry(resolvedCatalog);
            this.patternDefs = this.patternRegistry.defs;
            this.patternMap = this.patternRegistry.map;
            this.patternLabels = this.patternRegistry.labels;
            this.patternSources = this.patternRegistry.sources;

            // Mobile interaction state
            this.mobile = {
                mediaQuery: window.matchMedia('(max-width: 900px)'),
                isActive: false,
                zoomScale: 1,
                zoomSteps: [1, 1.25, 1.5, 2],
                colorRafId: null,
                depthMeterOpen: false
            };
            this.desktop = {
                depthMeterOpen: false
            };
            this.mobileBarReady = false;

            // Bind methods
            this.handleEditorMouseDown = this.handleEditorMouseDown.bind(this);
            this.handleEditorMouseMove = this.handleEditorMouseMove.bind(this);
            this.handleEditorMouseUp = this.handleEditorMouseUp.bind(this);
            this.handleGradientDrag = this.handleGradientDrag.bind(this);
            this.handleMobileViewportChange = this.handleMobileViewportChange.bind(this);
            this.handleWindowResize = this.handleWindowResize.bind(this);
        }

        // ============================================
        // INITIALIZATION
        // ============================================

        async init() {
            this.initializeNails();
            await this.preloadAssets();
            this.initUI();
            this.initEditor();
            this.initMobileBar();
            this.initHandOverlayTracking();

            await this.renderHand();
            await this.renderAllPreviews();
            await this.renderEditor();
            this.updateCharmList();
            this.updateMobileSelectorLabels();
            this.syncDepthMeter();

            // Remove loading state
            const loading = this.container.querySelector('.nail-builder__loading');
            if (loading) loading.remove();

            console.log('KIMCAO Nail Builder initialized!');
        }

        initHandOverlayTracking() {
            if (this.handOverlayResizeObserver) return;

            const refresh = () => this.queueHandOverlayRefresh([0, 120, 320]);
            const trackedElements = ['#handStage', '#handWrapper', '#nailsOverlay']
                .map((selector) => this.container.querySelector(selector))
                .filter(Boolean);

            if (typeof ResizeObserver === 'function' && trackedElements.length) {
                this.handOverlayResizeObserver = new ResizeObserver(() => {
                    this.queueHandOverlayRefresh([0, 120]);
                });

                trackedElements.forEach((element) => {
                    this.handOverlayResizeObserver.observe(element);
                });
            }

            window.addEventListener('resize', this.handleWindowResize, { passive: true });
            window.addEventListener('orientationchange', this.handleWindowResize, { passive: true });
            window.addEventListener('load', refresh, { once: true });
            refresh();
        }

        handleWindowResize() {
            this.queueHandOverlayRefresh([0, 120]);
        }

        queueHandOverlayRefresh(delays = [0]) {
            const normalizedDelays = Array.isArray(delays) ? delays : [delays];

            if (this.handOverlayRefreshFrame !== null) {
                cancelAnimationFrame(this.handOverlayRefreshFrame);
            }

            this.handOverlayRefreshFrame = requestAnimationFrame(() => {
                this.handOverlayRefreshFrame = null;
                this.renderNailsOnHand().catch((error) => {
                    console.error('Failed to refresh hand overlay', error);
                });
            });

            this.handOverlayRefreshTimers.forEach((timerId) => clearTimeout(timerId));
            this.handOverlayRefreshTimers = [];

            normalizedDelays
                .filter((delay) => Number.isFinite(delay) && delay > 0)
                .forEach((delay) => {
                    const timerId = window.setTimeout(() => {
                        this.renderNailsOnHand().catch((error) => {
                            console.error('Failed to refresh hand overlay', error);
                        });
                    }, delay);

                    this.handOverlayRefreshTimers.push(timerId);
                });
        }

        initializeNails() {
            ['left', 'right'].forEach(hand => {
                const handState = this.state.hands[hand] || this.createDefaultHandState(this.options.defaultShape);
                handState.shapeSet = SHAPES.includes(handState.shapeSet) ? handState.shapeSet : this.options.defaultShape;
                handState.activeFinger = FINGERS.includes(handState.activeFinger) ? handState.activeFinger : 'thumb';
                handState.nails = handState.nails && typeof handState.nails === 'object' ? handState.nails : {};

                FINGERS.forEach(finger => {
                    handState.nails[finger] = this.normalizeNailDesign(handState.nails[finger]);
                });

                this.state.hands[hand] = handState;
            });
        }

        createDefaultHandState(shapeSet = 'almond') {
            const nails = {};
            FINGERS.forEach(finger => {
                nails[finger] = this.createDefaultNailDesign();
            });

            return {
                shapeSet: SHAPES.includes(shapeSet) ? shapeSet : 'almond',
                activeFinger: 'thumb',
                nails
            };
        }

        createDefaultNailDesign() {
            return {
                base: {
                    kind: 'solid',
                    color: '#f0ede8',
                    opacity: 0.55
                },
                pattern: null,
                charms: []
            };
        }

        createPatternRegistry(rawCatalog) {
            const patternsRaw = rawCatalog && Array.isArray(rawCatalog.patterns) ? rawCatalog.patterns : [];
            const dynamicPatterns = [];
            const seen = new Set();

            patternsRaw.forEach((pattern) => {
                const normalized = normalizeDynamicPattern(pattern);
                if (!normalized || seen.has(normalized.id)) return;
                seen.add(normalized.id);
                dynamicPatterns.push(normalized);
            });

            // Keep legacy defaults always visible, then layer dynamic patterns on top.
            const orderedDefs = [];
            const defsById = {};

            LEGACY_PATTERN_DEFS.forEach((legacyPattern) => {
                const next = { ...legacyPattern };
                defsById[next.id] = next;
                orderedDefs.push(next);
            });

            dynamicPatterns.forEach((dynamicPattern) => {
                if (!dynamicPattern || !dynamicPattern.id) return;

                if (defsById[dynamicPattern.id]) {
                    Object.assign(defsById[dynamicPattern.id], dynamicPattern);
                    return;
                }

                const next = { ...dynamicPattern };
                defsById[next.id] = next;
                orderedDefs.push(next);
            });

            const defs = [...orderedDefs, { ...FRENCH_TIP_PATTERN_DEF }];

            const map = defs.reduce((acc, pattern) => {
                acc[pattern.id] = pattern;
                return acc;
            }, {});

            // Always keep legacy definitions for backwards compatibility fallback.
            Object.values(LEGACY_PATTERN_MAP).forEach((legacyPattern) => {
                if (!map[legacyPattern.id]) map[legacyPattern.id] = legacyPattern;
            });
            map[FRENCH_TIP_PATTERN_DEF.id] = FRENCH_TIP_PATTERN_DEF;

            const labels = Object.values(map).reduce((acc, pattern) => {
                acc[pattern.id] = pattern.label;
                return acc;
            }, {});

            return {
                defs,
                map,
                labels,
                sources: {
                    usingDynamicCatalog: dynamicPatterns.length > 0,
                    dynamicCount: dynamicPatterns.length
                }
            };
        }

        getPatternDefinition(patternId) {
            return this.patternMap[patternId] || null;
        }

        normalizePatternAssetUrl(value) {
            if (!value) return null;
            if (typeof value === 'string') {
                const trimmed = value.trim();
                return trimmed || null;
            }

            if (typeof value === 'object') {
                if (typeof value.url === 'string' && value.url.trim()) return value.url.trim();
                if (typeof value.src === 'string' && value.src.trim()) return value.src.trim();
                if (typeof value.href === 'string' && value.href.trim()) return value.href.trim();
            }

            return null;
        }

        resolveDynamicPatternSource(patternDef, shape, finger) {
            if (!patternDef || patternDef.source !== 'metaobject') return null;

            const svgMap = patternDef.svgMap && typeof patternDef.svgMap === 'object'
                ? patternDef.svgMap
                : null;

            if (svgMap) {
                const shapesMap = svgMap.shapes && typeof svgMap.shapes === 'object'
                    ? svgMap.shapes
                    : null;

                if (shapesMap && shapesMap[shape]) {
                    const shapeEntry = shapesMap[shape];
                    if (typeof shapeEntry === 'string') {
                        const shapeLevel = this.normalizePatternAssetUrl(shapeEntry);
                        if (shapeLevel) return shapeLevel;
                    } else if (shapeEntry && typeof shapeEntry === 'object') {
                        const exact = this.normalizePatternAssetUrl(shapeEntry[finger]);
                        if (exact) return exact;

                        const shapeLevel = this.normalizePatternAssetUrl(
                            shapeEntry._default || shapeEntry.default || shapeEntry.all
                        );
                        if (shapeLevel) return shapeLevel;
                    }
                }

                const directShapeEntry = svgMap[shape];
                if (directShapeEntry) {
                    if (typeof directShapeEntry === 'string') {
                        const shapeLevel = this.normalizePatternAssetUrl(directShapeEntry);
                        if (shapeLevel) return shapeLevel;
                    } else if (typeof directShapeEntry === 'object') {
                        const exact = this.normalizePatternAssetUrl(directShapeEntry[finger]);
                        if (exact) return exact;

                        const shapeLevel = this.normalizePatternAssetUrl(
                            directShapeEntry._default || directShapeEntry.default || directShapeEntry.all
                        );
                        if (shapeLevel) return shapeLevel;
                    }
                }

                const universalFromMap = this.normalizePatternAssetUrl(
                    svgMap.universal || svgMap.svg_universal || svgMap.default
                );
                if (universalFromMap) return universalFromMap;
            }

            const universal = this.normalizePatternAssetUrl(patternDef.svgUniversal || patternDef.file);
            return universal || null;
        }

        resolvePatternSource(patternId, shape = null, finger = null) {
            const patternDef = this.getPatternDefinition(patternId);
            if (!patternDef || patternDef.procedural) return null;

            const normalizedShape = SHAPES.includes(shape) ? shape : this.state.shapeSet;
            const normalizedFinger = FINGERS.includes(finger) ? finger : this.state.activeFinger;

            const dynamicSource = this.resolveDynamicPatternSource(patternDef, normalizedShape, normalizedFinger);
            if (dynamicSource) return dynamicSource;

            const legacySource = LEGACY_PATTERN_FILES[patternId] || LEGACY_PATTERN_FILES.basedesign1;
            if (legacySource) return legacySource;

            return this.normalizePatternAssetUrl(patternDef.file);
        }

        collectPatternPreloadSources(options = {}) {
            const sources = new Set();
            const includeAllShapeFinger = options.includeAllShapeFinger === true;
            const includeLegacy = options.includeLegacy !== false;
            const preloadShape = SHAPES.includes(options.shape) ? options.shape : this.state.shapeSet;
            const preloadFinger = FINGERS.includes(options.finger) ? options.finger : this.state.activeFinger;
            const maxDynamicPatterns = Number.isFinite(options.maxDynamicPatterns)
                ? Math.max(0, options.maxDynamicPatterns)
                : Number.POSITIVE_INFINITY;
            let dynamicLoaded = 0;

            if (includeLegacy) {
                Object.values(LEGACY_PATTERN_FILES).forEach((legacySource) => {
                    sources.add(legacySource);
                });
            }

            this.patternDefs.forEach((patternDef) => {
                if (!patternDef || patternDef.procedural) return;

                const isDynamic = patternDef.source === 'metaobject';
                if (isDynamic && dynamicLoaded >= maxDynamicPatterns) return;
                if (isDynamic) dynamicLoaded += 1;

                if (includeAllShapeFinger) {
                    SHAPES.forEach((shape) => {
                        FINGERS.forEach((finger) => {
                            const source = this.resolvePatternSource(patternDef.id, shape, finger);
                            if (source) sources.add(source);
                        });
                    });
                } else {
                    const active = this.resolvePatternSource(patternDef.id, preloadShape, preloadFinger);
                    if (active) sources.add(active);
                }

                const universal = this.resolvePatternSource(patternDef.id, this.state.shapeSet, this.state.activeFinger);
                if (universal) sources.add(universal);
            });

            return Array.from(sources);
        }

        scheduleDeferredPatternPreload() {
            const idleCallback = typeof window !== 'undefined' && typeof window.requestIdleCallback === 'function'
                ? window.requestIdleCallback.bind(window)
                : (callback) => window.setTimeout(() => callback({ didTimeout: false, timeRemaining: () => 8 }), 700);

            const deferredSources = this.collectPatternPreloadSources({
                includeAllShapeFinger: !this.mobile.mediaQuery.matches,
                maxDynamicPatterns: Number.POSITIVE_INFINITY
            }).filter((source) => source && !this.svgCache[source]);

            if (!deferredSources.length) return;

            const runBatch = (deadline) => {
                let loaded = 0;
                while (
                    deferredSources.length > 0 &&
                    loaded < PATTERN_DEFERRED_PRELOAD_BATCH &&
                    (deadline.didTimeout || deadline.timeRemaining() > 3)
                ) {
                    const source = deferredSources.shift();
                    if (source) this.loadSVG(source);
                    loaded += 1;
                }

                if (deferredSources.length > 0) {
                    idleCallback(runBatch, { timeout: 1200 });
                }
            };

            idleCallback(runBatch, { timeout: 1000 });
        }

        normalizeNailDesign(rawDesign) {
            const defaultDesign = this.createDefaultNailDesign();
            if (!rawDesign || typeof rawDesign !== 'object') return defaultDesign;

            const design = {
                base: rawDesign.base && typeof rawDesign.base === 'object'
                    ? JSON.parse(JSON.stringify(rawDesign.base))
                    : defaultDesign.base,
                pattern: this.normalizePattern(rawDesign.pattern),
                charms: Array.isArray(rawDesign.charms)
                    ? rawDesign.charms.map(charm => ({ ...charm }))
                    : []
            };

            return design;
        }

        clamp(value, min, max) {
            return Math.max(min, Math.min(max, value));
        }

        normalizeFrenchTipDepth(value) {
            const next = Number(value);
            const valid = Number.isFinite(next) ? next : FRENCH_TIP_DEFAULT_DEPTH;
            return Math.round(this.clamp(valid, FRENCH_TIP_MIN_DEPTH, FRENCH_TIP_MAX_DEPTH));
        }

        normalizeFrenchTipCurvature(value) {
            const next = Number(value);
            const valid = Number.isFinite(next) ? next : FRENCH_TIP_DEFAULT_CURVATURE;
            return Math.round(this.clamp(valid, FRENCH_TIP_MIN_CURVATURE, FRENCH_TIP_MAX_CURVATURE));
        }

        normalizePattern(rawPattern) {
            if (!rawPattern || typeof rawPattern !== 'object') return null;

            const id = rawPattern.id;
            if (!id || !this.patternMap[id]) return null;

            const pattern = {
                ...rawPattern,
                id,
                enabled: rawPattern.enabled !== false,
                tint: typeof rawPattern.tint === 'string' ? rawPattern.tint : '#333333',
                opacity: Number.isFinite(Number(rawPattern.opacity))
                    ? this.clamp(Number(rawPattern.opacity), 0, 1)
                    : 1
            };

            if (id === 'frenchtip') {
                pattern.tipDepth = this.normalizeFrenchTipDepth(rawPattern.tipDepth);
                pattern.smileCurvature = this.normalizeFrenchTipCurvature(rawPattern.smileCurvature);
            }

            return pattern;
        }

        tintSvgFill(svgText, color) {
            if (!svgText) return svgText;
            try {
                const parser = new DOMParser();
                const doc = parser.parseFromString(svgText, 'image/svg+xml');
                const svg = doc.querySelector('svg');
                if (!svg) return svgText;

                svg.querySelectorAll('script, style').forEach(node => node.remove());
                svg.querySelectorAll('[class]').forEach(node => node.removeAttribute('class'));

                svg.querySelectorAll('path, circle, ellipse, rect, polygon, polyline').forEach(node => {
                    node.setAttribute('fill', color);
                    node.removeAttribute('style');
                });

                return new XMLSerializer().serializeToString(svg);
            } catch (_error) {
                return svgText
                    .replace(/fill:\s*#[0-9a-fA-F]{3,8}/g, `fill:${color}`)
                    .replace(/fill:\s*rgb\([^)]+\)/g, `fill:${color}`)
                    .replace(/fill="\s*#[0-9a-fA-F]{3,8}\s*"/g, `fill="${color}"`)
                    .replace(/fill="\s*rgb\([^)]+\)\s*"/g, `fill="${color}"`);
            }
        }

        applyImportedDesign(imported) {
            if (!imported || typeof imported !== 'object') return false;

            if (imported.hands && typeof imported.hands === 'object') {
                ['left', 'right'].forEach(hand => {
                    const incoming = imported.hands[hand];
                    if (!incoming || typeof incoming !== 'object') return;

                    const nextHandState = this.createDefaultHandState(this.options.defaultShape);
                    if (SHAPES.includes(incoming.shapeSet)) nextHandState.shapeSet = incoming.shapeSet;
                    if (FINGERS.includes(incoming.activeFinger)) nextHandState.activeFinger = incoming.activeFinger;
                    if (incoming.nails && typeof incoming.nails === 'object') {
                        FINGERS.forEach(finger => {
                            nextHandState.nails[finger] = this.normalizeNailDesign(incoming.nails[finger]);
                        });
                    }
                    this.state.hands[hand] = nextHandState;
                });
            } else if (imported.nails && typeof imported.nails === 'object') {
                const targetHand = imported.hand === 'right' ? 'right' : this.state.hand;
                const nextHandState = this.createDefaultHandState(this.options.defaultShape);
                if (SHAPES.includes(imported.shapeSet)) nextHandState.shapeSet = imported.shapeSet;
                if (FINGERS.includes(imported.activeFinger)) nextHandState.activeFinger = imported.activeFinger;
                FINGERS.forEach(finger => {
                    nextHandState.nails[finger] = this.normalizeNailDesign(imported.nails[finger]);
                });
                this.state.hands[targetHand] = nextHandState;
            }

            if (imported.hand === 'left' || imported.hand === 'right') {
                this.state.hand = imported.hand;
            }

            this.initializeNails();
            return true;
        }

        // ============================================
        // ASSET LOADING
        // ============================================

        async loadSVG(filename) {
            if (this.svgCache[filename]) return this.svgCache[filename];

            try {
                const isAbsoluteUrl = /^https?:\/\//i.test(filename)
                    || filename.startsWith('//')
                    || filename.startsWith('data:')
                    || filename.startsWith('blob:')
                    || filename.startsWith('/');
                const url = isAbsoluteUrl ? filename : this.options.assetBasePath + filename;
                const response = await fetch(url);
                if (!response.ok) {
                    throw new Error(`HTTP ${response.status} for ${url}`);
                }

                const text = await response.text();
                if (!/<svg[\s>]/i.test(text)) {
                    throw new Error(`Expected SVG markup from ${url}`);
                }

                this.svgCache[filename] = text;

                // Extract path data
                const parser = new DOMParser();
                const doc = parser.parseFromString(text, 'image/svg+xml');
                const paths = doc.querySelectorAll('path, circle, ellipse');
                const svg = doc.querySelector('svg');
                const viewBox = svg ? svg.getAttribute('viewBox') : null;

                this.svgPathCache[filename] = {
                    paths: Array.from(paths).map(p => p.outerHTML),
                    viewBox: viewBox
                };

                return text;
            } catch (e) {
                if (!this.failedAssetLoads.has(filename)) {
                    this.failedAssetLoads.add(filename);

                    if (this.failedAssetLoads.size === 1 && typeof this.showToast === 'function') {
                        this.showToast('Builder assets are missing or invalid. Reinstall every file from the package zip.');
                    }
                }

                console.error(`Failed to load SVG: ${filename}`, e);
                return null;
            }
        }

        async preloadAssets() {
            const promises = [];
            const preloadAllShapes = !this.mobile.mediaQuery.matches;
            const shapeSetsToPreload = preloadAllShapes ? SHAPES : [this.state.shapeSet];
            const maxDynamicPatterns = this.mobile.mediaQuery.matches
                ? PATTERN_PRELOAD_LIMIT_MOBILE
                : PATTERN_PRELOAD_LIMIT_DESKTOP;

            // Load hand
            promises.push(this.loadSVG('KC_lefthand.svg'));

            // On mobile preload only the active shape set; lazy-load others on demand.
            for (const shape of shapeSetsToPreload) {
                for (const finger of FINGERS) {
                    const file = NAIL_FILES[shape][finger];
                    if (file) promises.push(this.loadSVG(file));
                }
            }

            // Load dynamic/legacy pattern sources for the active shape/finger first.
            for (const patternSource of this.collectPatternPreloadSources({
                includeAllShapeFinger: false,
                maxDynamicPatterns,
                shape: this.state.shapeSet,
                finger: this.state.activeFinger
            })) {
                promises.push(this.loadSVG(patternSource));
            }

            // Load 3D shape
            promises.push(this.loadSVG('KC_3dshape-1.svg'));

            await Promise.all(promises);
            this.scheduleDeferredPatternPreload();
            console.log('All assets loaded');
        }

        // ============================================
        // UI INITIALIZATION
        // ============================================

        initUI() {
            // Color presets
            const presetsContainer = this.container.querySelector('#colorPresets');
            if (presetsContainer) {
                COLOR_PRESETS.forEach(color => {
                    const preset = document.createElement('div');
                    preset.className = 'nail-builder__color-preset';
                    preset.style.background = color;
                    preset.onclick = () => {
                        const baseColorInput = this.container.querySelector('#baseColor');
                        if (baseColorInput) baseColorInput.value = color;
                        this.updateBaseColor(color);
                    };
                    presetsContainer.appendChild(preset);
                });
            }

            // Nude presets
            const nudeContainer = this.container.querySelector('#nudePresets');
            if (nudeContainer) {
                NUDE_PRESETS.forEach(nude => {
                    const preset = document.createElement('div');
                    preset.className = 'nail-builder__color-preset';
                    preset.style.background = nude.color;
                    preset.title = nude.name;
                    preset.dataset.nudeId = nude.id;
                    preset.onclick = () => this.selectNudePreset(nude.id, nude.color);
                    nudeContainer.appendChild(preset);
                });
            }

            // Pattern grid
            const patternGrid = this.container.querySelector('#patternGrid');
            if (patternGrid) {
                const patternNone = document.createElement('div');
                patternNone.className = 'nail-builder__pattern-item active';
                patternNone.innerHTML = '<span style="font-size: 0.7rem; color: var(--nb-text-secondary);">None</span>';
                patternNone.dataset.pattern = 'none';
                patternNone.onclick = () => this.selectPattern('none');
                patternGrid.appendChild(patternNone);

                this.patternDefs.forEach(patternDef => {
                    const patternId = patternDef.id;
                    const item = document.createElement('div');
                    item.className = 'nail-builder__pattern-item';
                    item.dataset.pattern = patternId;
                    item.title = patternDef.label;
                    item.innerHTML = `<div id="pattern-preview-${patternId}"></div>`;
                    item.onclick = () => this.selectPattern(patternId);
                    patternGrid.appendChild(item);

                    this.loadPatternPreview(patternId);
                });
            }

            // Charm grid
            const charmGrid = this.container.querySelector('#charmGrid');
            if (charmGrid) {
                CHARM_TYPES.forEach(charm => {
                    const item = document.createElement('div');
                    item.className = 'nail-builder__charm-item';
                    item.dataset.charm = charm.id;
                    item.title = charm.name;
                    item.onclick = () => this.addCharm(charm.id);

                    const canvas = document.createElement('canvas');
                    canvas.width = 80;
                    canvas.height = 80;
                    item.appendChild(canvas);
                    this.renderCharmPreview(canvas, charm.id, '#ff69b4');

                    charmGrid.appendChild(item);
                });
            }

            this.bindControlEvents();
        }

        bindControlEvents() {
            this.container.querySelectorAll('[data-hand]').forEach(btn => {
                btn.addEventListener('click', () => this.selectHand(btn.dataset.hand));
            });

            this.container.querySelectorAll('[data-shape]').forEach(btn => {
                btn.addEventListener('click', () => this.selectShape(btn.dataset.shape));
            });

            this.container.querySelectorAll('[data-base]').forEach(btn => {
                btn.addEventListener('click', () => this.selectBaseMode(btn.dataset.base));
            });

            const baseColorInput = this.container.querySelector('#baseColor');
            if (baseColorInput) {
                baseColorInput.addEventListener('input', (event) => this.updateBaseColor(event.target.value));
            }

            const baseOpacityInput = this.container.querySelector('#baseOpacity');
            if (baseOpacityInput) {
                baseOpacityInput.addEventListener('input', (event) => this.updateBaseOpacity(parseInt(event.target.value, 10)));
            }

            const nudeOpacityInput = this.container.querySelector('#nudeOpacity');
            if (nudeOpacityInput) {
                nudeOpacityInput.addEventListener('input', (event) => this.updateNudeOpacity(parseInt(event.target.value, 10)));
            }

            const metallicIntensityInput = this.container.querySelector('#metallicIntensity');
            if (metallicIntensityInput) {
                metallicIntensityInput.addEventListener('input', (event) => this.updateMetallicIntensity(parseInt(event.target.value, 10)));
            }

            ['#gradStart', '#gradMid', '#gradEnd'].forEach(selector => {
                const input = this.container.querySelector(selector);
                if (!input) return;
                input.addEventListener('input', () => this.updateGradient());
            });

            const midpointToggle = this.container.querySelector('#useMidpoint');
            if (midpointToggle) {
                midpointToggle.addEventListener('click', () => this.toggleMidpoint());
            }

            const splitToggle = this.container.querySelector('#splitToggle');
            if (splitToggle) {
                splitToggle.addEventListener('click', () => this.toggleSplit());
            }

            const splitPositionInput = this.container.querySelector('#splitPosition');
            if (splitPositionInput) {
                splitPositionInput.addEventListener('input', (event) => this.updateSplitPosition(parseInt(event.target.value, 10)));
            }

            const patternTintInput = this.container.querySelector('#patternTint');
            if (patternTintInput) {
                patternTintInput.addEventListener('input', (event) => this.updatePatternTint(event.target.value));
            }

            const patternOpacityInput = this.container.querySelector('#patternOpacity');
            if (patternOpacityInput) {
                patternOpacityInput.addEventListener('input', (event) => this.updatePatternOpacity(parseInt(event.target.value, 10)));
            }

            const tipDepthInput = this.container.querySelector('#patternTipDepth');
            if (tipDepthInput) {
                tipDepthInput.addEventListener('input', (event) => this.updateFrenchTipDepth(event.target.value));
            }

            const smileInput = this.container.querySelector('#patternSmileCurvature');
            if (smileInput) {
                smileInput.addEventListener('input', (event) => this.updateFrenchTipCurvature(event.target.value));
            }

            const depthSlider = this.container.querySelector('#depthSlider');
            if (depthSlider) {
                depthSlider.addEventListener('input', (event) => this.updateDepthFromMeter(event.target.value));
            }
        }

        normalizeHexColor(value, fallback) {
            if (typeof value !== 'string') return fallback;
            const normalized = value.trim().toLowerCase();
            return /^#([0-9a-f]{3}|[0-9a-f]{6})$/.test(normalized) ? normalized : fallback;
        }

        getEditableBaseTarget(design = this.state.nails[this.state.activeFinger]) {
            if (!design || !design.base || typeof design.base !== 'object') return null;
            if (design.base.kind === 'split' && design.base.bottom && typeof design.base.bottom === 'object') {
                return design.base.bottom;
            }
            return design.base;
        }

        getEditableBaseKind(design = this.state.nails[this.state.activeFinger]) {
            const editable = this.getEditableBaseTarget(design);
            return editable && editable.kind ? editable.kind : 'solid';
        }

        syncGradientControlsFromBase(base) {
            if (!base || base.kind !== 'gradient' || !base.gradient) return;

            const grad = base.gradient;
            const safeUnit = (value, fallback) => {
                const numeric = Number(value);
                return Number.isFinite(numeric) ? this.clamp(numeric, 0, 1) : fallback;
            };
            const startHandle = {
                x: safeUnit(grad.x1, 0.5),
                y: safeUnit(grad.y1, 0)
            };
            const endHandle = {
                x: safeUnit(grad.x2, 0.5),
                y: safeUnit(grad.y2, 1)
            };
            this.editorState.gradientHandles.start = startHandle;
            this.editorState.gradientHandles.end = endHandle;

            const stops = Array.isArray(grad.stops)
                ? grad.stops
                    .map(stop => ({
                        t: safeUnit(stop.t, 0.5),
                        color: this.normalizeHexColor(stop.color, '#f0ede8')
                    }))
                    .sort((a, b) => a.t - b.t)
                : [];

            const startStop = stops[0] || { t: 0, color: '#f0ede8' };
            const endStop = stops[stops.length - 1] || { t: 1, color: '#fad0c4' };
            const midStop = stops.find(stop => stop.t > 0.01 && stop.t < 0.99) || null;

            const gradStartInput = this.container.querySelector('#gradStart');
            const gradMidInput = this.container.querySelector('#gradMid');
            const gradEndInput = this.container.querySelector('#gradEnd');
            if (gradStartInput) gradStartInput.value = startStop.color;
            if (gradMidInput) gradMidInput.value = midStop ? midStop.color : '#ff9a9e';
            if (gradEndInput) gradEndInput.value = endStop.color;

            const midpointToggle = this.container.querySelector('#useMidpoint');
            if (midpointToggle) midpointToggle.classList.toggle('active', !!midStop);

            if (midStop) {
                this.editorState.gradientHandles.mid = {
                    x: startHandle.x + (endHandle.x - startHandle.x) * midStop.t,
                    y: startHandle.y + (endHandle.y - startHandle.y) * midStop.t
                };
            } else {
                this.editorState.gradientHandles.mid = {
                    x: (startHandle.x + endHandle.x) / 2,
                    y: (startHandle.y + endHandle.y) / 2
                };
            }
        }

        createBaseConfigByMode(mode, currentBase = null) {
            const depth = this.getDepthPercent() / 100;

            if (mode === 'solid') {
                const baseColorInput = this.container.querySelector('#baseColor');
                return {
                    kind: 'solid',
                    color: this.normalizeHexColor(
                        baseColorInput ? baseColorInput.value : (currentBase && currentBase.color),
                        '#f0ede8'
                    ),
                    opacity: currentBase && currentBase.kind === 'solid'
                        ? this.clamp(Number(currentBase.opacity ?? depth), 0.2, 1)
                        : depth
                };
            }

            if (mode === 'nude') {
                return {
                    kind: 'nude',
                    nudeId: currentBase && currentBase.kind === 'nude' && currentBase.nudeId
                        ? currentBase.nudeId
                        : NUDE_PRESETS[0].id,
                    opacity: currentBase && currentBase.kind === 'nude'
                        ? this.clamp(Number(currentBase.opacity ?? depth), 0.2, 1)
                        : depth
                };
            }

            if (mode === 'metallic') {
                return {
                    kind: 'metallic',
                    metal: 'silver',
                    intensity: currentBase && currentBase.kind === 'metallic'
                        ? this.clamp(Number(currentBase.intensity ?? depth), 0.2, 1)
                        : depth
                };
            }

            const gradStartInput = this.container.querySelector('#gradStart');
            const gradEndInput = this.container.querySelector('#gradEnd');
            if (currentBase && currentBase.kind === 'gradient' && currentBase.gradient) {
                return {
                    kind: 'gradient',
                    gradient: JSON.parse(JSON.stringify(currentBase.gradient))
                };
            }

            return {
                kind: 'gradient',
                gradient: {
                    x1: this.editorState.gradientHandles.start.x,
                    y1: this.editorState.gradientHandles.start.y,
                    x2: this.editorState.gradientHandles.end.x,
                    y2: this.editorState.gradientHandles.end.y,
                    stops: [
                        { t: 0, color: this.normalizeHexColor(gradStartInput ? gradStartInput.value : '', '#f0ede8'), opacity: depth },
                        { t: 1, color: this.normalizeHexColor(gradEndInput ? gradEndInput.value : '', '#fad0c4'), opacity: depth }
                    ]
                }
            };
        }

        initMobileBar() {
            const mobileBar = this.container.querySelector('.nail-builder__mobile-bar');
            if (!mobileBar) return;

            if (!this.mobileBarReady) {
                this.container.querySelectorAll('[data-mobile-selector]').forEach(btn => {
                    btn.addEventListener('click', () => this.handleMobileSelector(btn.dataset.mobileSelector));
                });

                const dropdown = this.container.querySelector('#mobileDropdown');
                if (dropdown) {
                    dropdown.addEventListener('click', (event) => {
                        if (event.target === dropdown) {
                            this.closeMobileDropdown();
                        }
                    });
                }

                if (!this.depthMeterDismissHandler) {
                    this.depthMeterDismissHandler = (event) => {
                        if (this.mobile.isActive || !this.desktop.depthMeterOpen) return;

                        const meter = this.container.querySelector('#depthMeter');
                        const depthButton = this.container.querySelector('.nail-builder__depth-tool');
                        const target = event.target;

                        if (!meter || !target) return;
                        if (meter.contains(target) || (depthButton && depthButton.contains(target))) return;

                        this.desktop.depthMeterOpen = false;
                        this.syncDepthMeter();
                    };
                    document.addEventListener('pointerdown', this.depthMeterDismissHandler);
                }

                const mobileColorPicker = this.container.querySelector('#mobileColorPicker');
                if (mobileColorPicker) {
                    mobileColorPicker.addEventListener('input', (event) => {
                        this.applyMobileColor(event.target.value, { source: 'picker' });
                    });
                    mobileColorPicker.addEventListener('change', (event) => {
                        this.applyMobileColor(event.target.value, { source: 'picker' });
                    });
                }

                this.initMobileColorScroll();

                if (typeof this.mobile.mediaQuery.addEventListener === 'function') {
                    this.mobile.mediaQuery.addEventListener('change', this.handleMobileViewportChange);
                } else if (typeof this.mobile.mediaQuery.addListener === 'function') {
                    this.mobile.mediaQuery.addListener(this.handleMobileViewportChange);
                }

                window.addEventListener('resize', this.handleMobileViewportChange, { passive: true });
                this.mobileBarReady = true;
            }

            this.handleMobileViewportChange();
        }

        handleMobileViewportChange() {
            this.mobile.isActive = this.mobile.mediaQuery.matches;

            if (!this.mobile.isActive) {
                this.closeMobileDropdown();
                this.mobile.zoomScale = 1;
                this.mobile.depthMeterOpen = false;
                this.desktop.depthMeterOpen = false;
                const handWrapper = this.container.querySelector('#handWrapper');
                if (handWrapper) {
                    handWrapper.style.transform = 'scale(1)';
                }
            }

            requestAnimationFrame(() => {
                const header = this.container.querySelector('.nail-builder__header');
                const mobileBar = this.container.querySelector('.nail-builder__mobile-bar');

                if (header) {
                    this.container.style.setProperty('--nb-mobile-header-height', `${Math.ceil(header.getBoundingClientRect().height)}px`);
                }
                if (mobileBar) {
                    this.container.style.setProperty('--nb-mobile-bar-height', `${Math.ceil(mobileBar.getBoundingClientRect().height)}px`);
                }
                this.syncDepthMeter();
            });
        }

        initMobileColorScroll() {
            const scrollContainer = this.container.querySelector('#mobileColorScroll');
            if (!scrollContainer || scrollContainer.dataset.ready === 'true') return;

            COLOR_PRESETS.forEach(color => {
                const swatch = document.createElement('button');
                swatch.type = 'button';
                swatch.className = 'nail-builder__mobile-color-swatch';
                swatch.dataset.color = color.toLowerCase();
                swatch.style.background = color;
                swatch.setAttribute('aria-label', `Apply color ${color}`);
                swatch.addEventListener('click', () => this.applyMobileColor(color, { source: 'swatch' }));
                scrollContainer.appendChild(swatch);
            });

            scrollContainer.addEventListener('touchstart', () => {}, { passive: true });
            scrollContainer.addEventListener('touchmove', () => {}, { passive: true });

            scrollContainer.dataset.ready = 'true';
            const editableBase = this.getEditableBaseTarget();
            this.setActiveMobileSwatch(editableBase && editableBase.kind === 'solid' ? editableBase.color : '#f0ede8');
        }

        setActiveMobileSwatch(color) {
            const normalized = (color || '').toLowerCase();
            this.container.querySelectorAll('.nail-builder__mobile-color-swatch').forEach(swatch => {
                swatch.classList.toggle('active', swatch.dataset.color === normalized);
            });
            this.container.dispatchEvent(new CustomEvent('nailbuilder:mobile-color-selection-change'));
        }

        applyMobileColor(color, options = {}) {
            const source = options && options.source ? options.source : 'swatch';
            if (this.mobile.colorRafId) {
                cancelAnimationFrame(this.mobile.colorRafId);
            }

            this.mobile.colorRafId = requestAnimationFrame(() => {
                this.mobile.colorRafId = null;

                if (this.getEditableBaseKind() !== 'solid') {
                    this.selectBaseMode('solid');
                }

                const baseColorInput = this.container.querySelector('#baseColor');
                if (baseColorInput) {
                    baseColorInput.value = color;
                }

                this.setActiveMobileSwatch(color);
                this.updateBaseColor(color, { syncMobilePicker: source === 'picker' });
            });
        }

        handleMobileSelector(selectorType) {
            const dropdown = this.container.querySelector('#mobileDropdown');
            const content = this.container.querySelector('#mobileDropdownContent');
            if (!dropdown || !content) return;

            if (selectorType === 'base') {
                this.openBaseEditorDropdown(dropdown, content);
                return;
            }

            if (selectorType === 'charms') {
                this.openMobileCharmEditor(dropdown, content);
                return;
            }

            const options = this.getMobileSelectorOptions(selectorType);
            if (options.length === 0) return;

            content.innerHTML = '';
            content.classList.toggle('nail-builder__mobile-dropdown-content--charms', selectorType === 'charms');
            content.classList.remove('nail-builder__mobile-dropdown-content--base-editor');
            content.classList.remove('nail-builder__mobile-dropdown-content--charms-editor');

            options.forEach(option => {
                const button = document.createElement('button');
                button.type = 'button';
                button.className = `nail-builder__mobile-option ${option.active ? 'active' : ''}`;
                if (selectorType === 'pattern' && option.patternId && option.patternId !== 'none') {
                    button.classList.add('nail-builder__mobile-option--pattern');
                    button.appendChild(this.createPatternPreview(option.patternId));
                } else if (selectorType === 'charms' && option.charmId) {
                    button.classList.add('nail-builder__mobile-option--charm');
                    const previewCanvas = document.createElement('canvas');
                    previewCanvas.width = 56;
                    previewCanvas.height = 56;
                    previewCanvas.className = 'nail-builder__mobile-charm-preview';
                    this.renderCharmPreview(previewCanvas, option.charmId, '#ff69b4');
                    button.appendChild(previewCanvas);
                }

                const label = document.createElement('span');
                label.textContent = option.label;
                button.appendChild(label);

                button.addEventListener('click', () => {
                    option.onSelect();
                    this.closeMobileDropdown();
                });
                content.appendChild(button);
            });

            if (selectorType === 'pattern') {
                const design = this.state.nails[this.state.activeFinger];
                if (design.pattern && design.pattern.enabled && this.isFrenchTipPattern(design.pattern)) {
                    this.injectMobileFrenchTipControls(content, design.pattern);
                }
            }

            dropdown.dataset.selectorType = selectorType;
            dropdown.classList.add('is-open');
        }

        openBaseEditorDropdown(dropdown, content) {
            const design = this.state.nails[this.state.activeFinger];
            const editableBase = this.getEditableBaseTarget(design);
            if (!editableBase) return;

            if (editableBase.kind === 'gradient') {
                this.syncGradientControlsFromBase(editableBase);
            }

            const editableKind = this.getEditableBaseKind(design);
            const splitEnabled = design.base.kind === 'split';
            const splitPercent = Math.round(this.clamp(Number(design.base.splitT ?? 0.5), 0.2, 0.8) * 100);

            const midpointToggle = this.container.querySelector('#useMidpoint');
            const useMid = !!(midpointToggle && midpointToggle.classList.contains('active'));
            const gradStartInput = this.container.querySelector('#gradStart');
            const gradMidInput = this.container.querySelector('#gradMid');
            const gradEndInput = this.container.querySelector('#gradEnd');

            const gradStart = this.normalizeHexColor(gradStartInput ? gradStartInput.value : '', '#f0ede8');
            const gradMid = this.normalizeHexColor(gradMidInput ? gradMidInput.value : '', '#ff9a9e');
            const gradEnd = this.normalizeHexColor(gradEndInput ? gradEndInput.value : '', '#fad0c4');

            content.classList.remove('nail-builder__mobile-dropdown-content--charms');
            content.classList.remove('nail-builder__mobile-dropdown-content--charms-editor');
            content.classList.add('nail-builder__mobile-dropdown-content--base-editor');
            content.innerHTML = `
                <div class="nail-builder__mobile-base-editor">
                    <div class="nail-builder__mobile-base-title">paint editor</div>
                    <div class="nail-builder__mobile-base-modes">
                        ${[
                            { id: 'solid', label: 'solid' },
                            { id: 'nude', label: 'nude' },
                            { id: 'metallic', label: 'metallic' },
                            { id: 'gradient', label: 'gradient' }
                        ].map(mode => `
                            <button type="button" class="nail-builder__mobile-base-mode ${editableKind === mode.id ? 'active' : ''}" data-mobile-base-mode="${mode.id}">
                                ${mode.label}
                            </button>
                        `).join('')}
                    </div>

                    <div class="nail-builder__mobile-base-split">
                        <div class="nail-builder__mobile-base-split-row">
                            <span>split nail</span>
                            <button type="button" class="nail-builder__mobile-switch ${splitEnabled ? 'active' : ''}" data-mobile-split-toggle aria-pressed="${splitEnabled ? 'true' : 'false'}" aria-label="Toggle split nail">
                                <span></span>
                            </button>
                        </div>
                        ${splitEnabled ? `
                            <div class="nail-builder__mobile-base-split-position">
                                <div class="nail-builder__mobile-base-split-label">
                                    <span>split position</span>
                                    <span data-mobile-split-position-value>${splitPercent}%</span>
                                </div>
                                <input type="range" min="20" max="80" value="${splitPercent}" data-mobile-split-position>
                            </div>
                        ` : ''}
                    </div>

                    ${editableKind === 'gradient' ? `
                        <div class="nail-builder__mobile-gradient-editor">
                            <div class="nail-builder__mobile-gradient-head">
                                <span>gradient editor</span>
                                <button type="button" class="nail-builder__mobile-midpoint-toggle ${useMid ? 'active' : ''}" data-mobile-midpoint-toggle>
                                    midpoint ${useMid ? 'on' : 'off'}
                                </button>
                            </div>
                            <div class="nail-builder__mobile-gradient-colors">
                                <label>
                                    <span>start</span>
                                    <input type="color" value="${gradStart}" data-mobile-grad-start>
                                </label>
                                <label>
                                    <span>mid</span>
                                    <input type="color" value="${gradMid}" data-mobile-grad-mid ${useMid ? '' : 'disabled'}>
                                </label>
                                <label>
                                    <span>end</span>
                                    <input type="color" value="${gradEnd}" data-mobile-grad-end>
                                </label>
                            </div>
                        </div>
                    ` : ''}
                </div>
            `;

            this.bindMobileBaseEditorEvents(content);

            dropdown.dataset.selectorType = 'base';
            dropdown.classList.add('is-open');
        }

        bindMobileBaseEditorEvents(content) {
            if (!content || content.dataset.baseEditorBound === 'true') return;

            content.addEventListener('click', (event) => {
                const modeButton = event.target.closest('[data-mobile-base-mode]');
                if (modeButton) {
                    this.selectBaseMode(modeButton.dataset.mobileBaseMode);
                    return;
                }

                const splitToggle = event.target.closest('[data-mobile-split-toggle]');
                if (splitToggle) {
                    this.toggleSplit();
                    return;
                }

                const midpointToggle = event.target.closest('[data-mobile-midpoint-toggle]');
                if (midpointToggle) {
                    this.toggleMidpoint();
                }
            });

            content.addEventListener('input', (event) => {
                const splitPosition = event.target.closest('[data-mobile-split-position]');
                if (splitPosition) {
                    const value = parseInt(splitPosition.value, 10);
                    const desktopSplitInput = this.container.querySelector('#splitPosition');
                    if (desktopSplitInput) desktopSplitInput.value = `${value}`;
                    this.updateSplitPosition(value);
                    const splitValue = content.querySelector('[data-mobile-split-position-value]');
                    if (splitValue) splitValue.textContent = `${value}%`;
                    return;
                }

                const colorInput = event.target.closest('[data-mobile-grad-start], [data-mobile-grad-mid], [data-mobile-grad-end]');
                if (!colorInput) return;

                const mapping = {
                    mobileGradStart: '#gradStart',
                    mobileGradMid: '#gradMid',
                    mobileGradEnd: '#gradEnd'
                };

                const targetId = Object.keys(mapping).find((key) => colorInput.dataset[key] !== undefined);
                if (!targetId) return;

                const hiddenInput = this.container.querySelector(mapping[targetId]);
                if (hiddenInput) hiddenInput.value = colorInput.value;
                this.updateGradient();
            });

            content.dataset.baseEditorBound = 'true';
        }

        openMobileCharmEditor(dropdown, content) {
            const design = this.state.nails[this.state.activeFinger];

            content.classList.remove('nail-builder__mobile-dropdown-content--charms');
            content.classList.remove('nail-builder__mobile-dropdown-content--base-editor');
            content.classList.add('nail-builder__mobile-dropdown-content--charms-editor');

            content.innerHTML = `
                <div class="nail-builder__mobile-charms-panel">
                    <div class="nail-builder__mobile-charms-section">
                        <div class="nail-builder__mobile-charms-title">add charm</div>
                        <div class="nail-builder__mobile-charms-add-grid">
                            ${CHARM_TYPES.map(charm => `
                                <button type="button" class="nail-builder__mobile-option nail-builder__mobile-option--charm" data-mobile-add-charm="${charm.id}">
                                    <canvas width="28" height="28" class="nail-builder__mobile-charm-preview" data-mobile-add-charm-preview="${charm.id}"></canvas>
                                    <span>${charm.name}</span>
                                </button>
                            `).join('')}
                        </div>
                    </div>

                    <div class="nail-builder__mobile-charms-section">
                        <div class="nail-builder__mobile-charms-title">on this nail</div>
                        <div class="nail-builder__mobile-charms-hint">tap one, then drag it on the preview to move it</div>
                        <div class="nail-builder__mobile-charms-list">
                            ${design.charms.length === 0
                                ? '<div class="nail-builder__mobile-charms-empty">no charms added</div>'
                                : design.charms.map((charm, idx) => {
                                    const type = CHARM_TYPES.find(item => item.id === charm.type);
                                    const label = type ? type.name.toLowerCase() : charm.type;
                                    const tint = this.normalizeHexColor(charm.tint, '#ff69b4');
                                    return `
                                        <div class="nail-builder__mobile-charm-row ${this.editorState.selectedCharm === idx ? 'is-selected' : ''}">
                                            <button type="button" class="nail-builder__mobile-charm-select" data-mobile-select-charm="${idx}">
                                                <span class="nail-builder__mobile-charm-dot" style="--nb-charm-color: ${tint};"></span>
                                                <span>${label}</span>
                                            </button>
                                            <button type="button" class="nail-builder__mobile-charm-delete" data-mobile-delete-charm="${idx}">remove</button>
                                        </div>
                                    `;
                                }).join('')}
                        </div>
                    </div>
                </div>
            `;

            content.querySelectorAll('[data-mobile-add-charm-preview]').forEach((canvas) => {
                this.renderCharmPreview(canvas, canvas.dataset.mobileAddCharmPreview, '#ff69b4');
            });

            this.bindMobileCharmEditorEvents(content, dropdown);

            dropdown.dataset.selectorType = 'charms';
            dropdown.classList.add('is-open');
        }

        bindMobileCharmEditorEvents(content, dropdown) {
            content.querySelectorAll('[data-mobile-add-charm]').forEach((button) => {
                button.addEventListener('click', () => {
                    this.addCharm(button.dataset.mobileAddCharm);
                    this.closeMobileDropdown();
                    this.showToast('Drag the preview nail to reposition the charm');
                });
            });

            content.querySelectorAll('[data-mobile-select-charm]').forEach((button) => {
                button.addEventListener('click', () => {
                    this.selectCharmByIndex(parseInt(button.dataset.mobileSelectCharm, 10));
                    this.closeMobileDropdown();
                    this.showToast('Drag the preview nail to reposition the charm');
                });
            });

            content.querySelectorAll('[data-mobile-delete-charm]').forEach((button) => {
                button.addEventListener('click', () => {
                    this.removeCharm(parseInt(button.dataset.mobileDeleteCharm, 10));
                    this.openMobileCharmEditor(dropdown, content);
                });
            });
        }

        getMobileSelectorOptions(selectorType) {
            const design = this.state.nails[this.state.activeFinger];
            const patternId = design.pattern && design.pattern.enabled ? design.pattern.id : 'none';

            if (selectorType === 'hand') {
                return [
                    { label: 'Left', active: this.state.hand === 'left', onSelect: () => this.selectHand('left') },
                    { label: 'Right', active: this.state.hand === 'right', onSelect: () => this.selectHand('right') }
                ];
            }

            if (selectorType === 'finger') {
                return FINGERS.map(finger => ({
                    label: this.formatLabel(finger),
                    active: this.state.activeFinger === finger,
                    onSelect: () => this.selectFinger(finger)
                }));
            }

            if (selectorType === 'shape') {
                return SHAPES.map(shape => ({
                    label: this.formatLabel(shape),
                    active: this.state.shapeSet === shape,
                    onSelect: () => this.selectShape(shape)
                }));
            }

            if (selectorType === 'base') {
                const editableKind = this.getEditableBaseKind(design);
                return [
                    { id: 'solid', label: 'Solid' },
                    { id: 'nude', label: 'Nude' },
                    { id: 'metallic', label: 'Metallic' },
                    { id: 'gradient', label: 'Gradient' }
                ].map(base => ({
                    label: base.label,
                    active: editableKind === base.id,
                    onSelect: () => this.selectBaseMode(base.id)
                }));
            }

            if (selectorType === 'pattern') {
                return [
                    {
                        label: 'None',
                        active: patternId === 'none',
                        patternId: 'none',
                        onSelect: () => this.selectPattern('none')
                    },
                    ...this.patternDefs.map((pattern) => ({
                        label: pattern.label,
                        active: patternId === pattern.id,
                        patternId: pattern.id,
                        onSelect: () => this.selectPattern(pattern.id)
                    }))
                ];
            }

            if (selectorType === 'charms') {
                return CHARM_TYPES.map(charm => ({
                    label: charm.name,
                    active: false,
                    charmId: charm.id,
                    onSelect: () => this.addCharm(charm.id)
                }));
            }

            return [];
        }

        formatSignedPercent(value) {
            const rounded = Math.round(Number(value) || 0);
            return rounded > 0 ? `+${rounded}%` : `${rounded}%`;
        }

        injectMobileFrenchTipControls(container, pattern) {
            this.ensureFrenchTipPatternFields(pattern);

            const controls = document.createElement('div');
            controls.className = 'nail-builder__mobile-french-tip-controls';
            controls.innerHTML = `
                <div class="nail-builder__mobile-french-tip-row">
                    <div class="nail-builder__mobile-french-tip-label">
                        <span>tip depth</span>
                        <span data-mobile-tip-depth-value>${pattern.tipDepth}%</span>
                    </div>
                    <input type="range" min="${FRENCH_TIP_MIN_DEPTH}" max="${FRENCH_TIP_MAX_DEPTH}" value="${pattern.tipDepth}" data-mobile-tip-depth>
                </div>
                <div class="nail-builder__mobile-french-tip-row">
                    <div class="nail-builder__mobile-french-tip-label">
                        <span>smile curvature</span>
                        <span data-mobile-smile-value>${this.formatSignedPercent(pattern.smileCurvature)}</span>
                    </div>
                    <input type="range" min="${FRENCH_TIP_MIN_CURVATURE}" max="${FRENCH_TIP_MAX_CURVATURE}" value="${pattern.smileCurvature}" data-mobile-smile-curvature>
                </div>
            `;

            const depthInput = controls.querySelector('[data-mobile-tip-depth]');
            const smileInput = controls.querySelector('[data-mobile-smile-curvature]');

            if (depthInput) {
                depthInput.addEventListener('input', (event) => {
                    this.updateFrenchTipDepth(event.target.value);
                    const valueEl = controls.querySelector('[data-mobile-tip-depth-value]');
                    if (valueEl) valueEl.textContent = `${this.normalizeFrenchTipDepth(event.target.value)}%`;
                });
            }

            if (smileInput) {
                smileInput.addEventListener('input', (event) => {
                    this.updateFrenchTipCurvature(event.target.value);
                    const valueEl = controls.querySelector('[data-mobile-smile-value]');
                    if (valueEl) valueEl.textContent = this.formatSignedPercent(this.normalizeFrenchTipCurvature(event.target.value));
                });
            }

            container.appendChild(controls);
        }

        createPatternPreview(patternId) {
            const preview = document.createElement('span');
            preview.className = 'nail-builder__mobile-pattern-preview';

            const patternDef = this.getPatternDefinition(patternId);
            const previewImageUrl = this.normalizePatternAssetUrl(patternDef && patternDef.previewImage);
            if (previewImageUrl) {
                const img = document.createElement('img');
                img.src = previewImageUrl;
                img.alt = '';
                img.loading = 'lazy';
                img.decoding = 'async';
                img.setAttribute('aria-hidden', 'true');
                preview.appendChild(img);
                return preview;
            }

            if (patternId === 'frenchtip') {
                preview.innerHTML = this.createFrenchTipPreviewSvg('currentColor');
                const svg = preview.querySelector('svg');
                if (svg) {
                    svg.setAttribute('aria-hidden', 'true');
                    svg.style.width = '100%';
                    svg.style.height = '100%';
                }
                return preview;
            }

            const patternSource = this.resolvePatternSource(patternId, this.state.shapeSet, this.state.activeFinger);
            const svgText = patternSource ? this.svgCache[patternSource] : null;
            if (!svgText) {
                preview.textContent = '•';
                return preview;
            }

            preview.innerHTML = this.prepareSvgPreviewMarkup(svgText, 'currentColor');
            const svg = preview.querySelector('svg');
            if (svg) {
                svg.setAttribute('aria-hidden', 'true');
                svg.style.width = '100%';
                svg.style.height = '100%';
            }

            return preview;
        }

        closeMobileDropdown() {
            const dropdown = this.container.querySelector('#mobileDropdown');
            if (dropdown) {
                dropdown.classList.remove('is-open');
                delete dropdown.dataset.selectorType;
            }
            const content = this.container.querySelector('#mobileDropdownContent');
            if (content) {
                content.classList.remove('nail-builder__mobile-dropdown-content--base-editor');
                content.classList.remove('nail-builder__mobile-dropdown-content--charms-editor');
            }
        }

        zoomHand(direction) {
            const handWrapper = this.container.querySelector('#handWrapper');
            if (!handWrapper) return;

            const steps = this.mobile.zoomSteps;
            let currentIndex = steps.findIndex(step => Math.abs(step - this.mobile.zoomScale) < 0.01);
            if (currentIndex === -1) {
                currentIndex = steps.findIndex(step => step >= this.mobile.zoomScale);
                if (currentIndex === -1) currentIndex = steps.length - 1;
            }

            if (direction === 'in') {
                currentIndex = Math.min(steps.length - 1, currentIndex + 1);
            } else {
                currentIndex = Math.max(0, currentIndex - 1);
            }

            this.mobile.zoomScale = steps[currentIndex];
            handWrapper.style.transform = `scale(${this.mobile.zoomScale})`;
        }

        async captureScreenshot() {
            try {
                this.showToast('Saving screenshot...');

                const handStage = this.container.querySelector('#handStage');
                const stageRect = handStage ? handStage.getBoundingClientRect() : { width: 375, height: 640 };
                const exportWidth = Math.max(720, Math.round(stageRect.width * 2));
                const exportHeight = Math.max(1200, Math.round(stageRect.height * 2));
                const exportCanvas = document.createElement('canvas');
                exportCanvas.width = exportWidth;
                exportCanvas.height = exportHeight;

                await this.drawStageToCanvas(exportCanvas);

                const fileName = `kimcao-stage-${Date.now()}.png`;
                const blob = await new Promise((resolve, reject) => {
                    exportCanvas.toBlob((nextBlob) => {
                        if (nextBlob) {
                            resolve(nextBlob);
                        } else {
                            reject(new Error('Failed to create screenshot blob'));
                        }
                    }, 'image/png');
                });
                const dataUrl = exportCanvas.toDataURL('image/png');
                const saveState = await this.saveScreenshotBlob(blob, fileName);

                if (saveState === 'shared') {
                    this.showToast('Share sheet opened');
                } else if (saveState === 'opened') {
                    this.showToast('Opened image. Long-press to save.');
                } else if (saveState === 'cancelled') {
                    this.showToast('Share cancelled');
                } else {
                    this.showToast('Screenshot saved!');
                }
                return dataUrl;
            } catch (error) {
                console.error('Screenshot capture failed', error);
                this.showToast('Could not save screenshot');
                return null;
            }
        }

        async saveScreenshotBlob(blob, fileName) {
            const isMobileShareDevice = this.isLikelyIOS() || /Android/i.test(navigator.userAgent || '');
            const canShareFiles = typeof navigator !== 'undefined'
                && typeof navigator.share === 'function'
                && typeof navigator.canShare === 'function'
                && typeof File === 'function';

            if (canShareFiles && isMobileShareDevice) {
                try {
                    const file = new File([blob], fileName, { type: 'image/png' });
                    if (navigator.canShare({ files: [file] })) {
                        await navigator.share({
                            files: [file],
                            title: 'KIMCAO Nail Design'
                        });
                        return 'shared';
                    }
                } catch (error) {
                    if (error && error.name === 'AbortError') {
                        return 'cancelled';
                    }
                    console.warn('File share failed, using download fallback', error);
                }
            }

            const objectUrl = URL.createObjectURL(blob);
            try {
                const downloadLink = document.createElement('a');
                downloadLink.href = objectUrl;
                downloadLink.download = fileName;
                downloadLink.rel = 'noopener';
                downloadLink.style.display = 'none';
                document.body.appendChild(downloadLink);
                downloadLink.click();
                downloadLink.remove();

                if (this.isLikelyIOS()) {
                    const opened = window.open(objectUrl, '_blank', 'noopener');
                    if (opened) {
                        return 'opened';
                    }
                }

                return 'downloaded';
            } finally {
                window.setTimeout(() => URL.revokeObjectURL(objectUrl), 5000);
            }
        }

        isLikelyIOS() {
            const ua = navigator.userAgent || '';
            const platform = navigator.platform || '';
            return /iPad|iPhone|iPod/.test(ua) || (platform === 'MacIntel' && navigator.maxTouchPoints > 1);
        }

        resolveHandLayout(containerWidth, containerHeight) {
            const width = Number(containerWidth);
            const height = Number(containerHeight);
            const safeWidth = Number.isFinite(width) ? Math.max(0, width) : 0;
            const safeHeight = Number.isFinite(height) ? Math.max(0, height) : 0;

            if (!safeWidth || !safeHeight) {
                return {
                    scale: 0,
                    offsetX: 0,
                    offsetY: 0,
                    handWidth: 0,
                    handHeight: 0,
                    handVBWidth: HAND_VIEWBOX.width,
                    handVBHeight: HAND_VIEWBOX.height
                };
            }

            const scale = Math.min(
                (safeWidth * HAND_LAYOUT_FIT.widthRatio) / HAND_VIEWBOX.width,
                (safeHeight * HAND_LAYOUT_FIT.heightRatio) / HAND_VIEWBOX.height
            );
            const handWidth = HAND_VIEWBOX.width * scale;
            const handHeight = HAND_VIEWBOX.height * scale;

            return {
                scale,
                offsetX: (safeWidth - handWidth) / 2,
                offsetY: (safeHeight - handHeight) / 2,
                handWidth,
                handHeight,
                handVBWidth: HAND_VIEWBOX.width,
                handVBHeight: HAND_VIEWBOX.height
            };
        }

        parseViewBoxDimensions(viewBoxValue) {
            if (typeof viewBoxValue !== 'string') return null;

            const values = viewBoxValue.trim().split(/\s+/).map(Number);
            if (values.length !== 4 || values.some(value => Number.isNaN(value))) {
                return null;
            }

            const width = values[2];
            const height = values[3];
            if (!(width > 0) || !(height > 0)) return null;

            return { width, height };
        }

        async getNailViewBoxDimensions(shapeSet, finger) {
            const nailSet = NAIL_FILES[shapeSet] || NAIL_FILES[this.state.shapeSet];
            if (!nailSet) return null;

            const nailFile = nailSet[finger];
            if (!nailFile) return null;

            let pathData = this.svgPathCache[nailFile];
            if (!pathData) {
                await this.loadSVG(nailFile);
                pathData = this.svgPathCache[nailFile];
            }

            if (!pathData || !pathData.viewBox) return null;
            const viewBox = this.parseViewBoxDimensions(pathData.viewBox);
            if (!viewBox) return null;

            return { ...viewBox, nailFile };
        }

        async resolveHandNailTransforms(containerWidth, containerHeight, options = {}) {
            const shapeSet = SHAPES.includes(options.shapeSet) ? options.shapeSet : this.state.shapeSet;
            const hand = options.hand === 'right' ? 'right' : 'left';
            const isRightHand = hand === 'right';
            const layout = this.resolveHandLayout(containerWidth, containerHeight);
            const placements = DEFAULT_PLACEMENTS[shapeSet] || DEFAULT_PLACEMENTS[this.state.shapeSet];
            const transforms = [];

            if (!placements || !layout.scale) {
                return { isRightHand, layout, transforms };
            }

            for (const finger of FINGERS) {
                const placement = placements[finger];
                if (!placement) continue;

                const nailMetrics = await this.getNailViewBoxDimensions(shapeSet, finger);
                if (!nailMetrics) continue;

                const nailScale = layout.scale * placement.scale;
                let x = layout.offsetX + placement.x * layout.scale;
                const y = layout.offsetY + placement.y * layout.scale;

                if (isRightHand) {
                    x = containerWidth - x;
                }

                transforms.push({
                    finger,
                    x,
                    y,
                    width: nailMetrics.width * nailScale,
                    height: nailMetrics.height * nailScale,
                    rotation: isRightHand ? -placement.rotation : placement.rotation,
                    mirror: isRightHand,
                    zIndex: FINGER_LAYER_ORDER[finger] || 1
                });
            }

            return { isRightHand, layout, transforms };
        }

        resolveStageHandRectForExport(exportWidth, exportHeight) {
            const handStage = this.container.querySelector('#handStage');
            const handWrapper = this.container.querySelector('#handWrapper');

            if (handStage && handWrapper) {
                const stageRect = handStage.getBoundingClientRect();
                const wrapperRect = handWrapper.getBoundingClientRect();
                if (stageRect.width > 0 && stageRect.height > 0 && wrapperRect.width > 0 && wrapperRect.height > 0) {
                    return {
                        x: ((wrapperRect.left - stageRect.left) / stageRect.width) * exportWidth,
                        y: ((wrapperRect.top - stageRect.top) / stageRect.height) * exportHeight,
                        width: (wrapperRect.width / stageRect.width) * exportWidth,
                        height: (wrapperRect.height / stageRect.height) * exportHeight
                    };
                }
            }

            const handAspect = HAND_VIEWBOX.width / HAND_VIEWBOX.height;
            let handHeight = exportHeight * 0.88;
            let handWidth = handHeight * handAspect;

            if (handWidth > exportWidth * 0.82) {
                handWidth = exportWidth * 0.82;
                handHeight = handWidth / handAspect;
            }

            return {
                x: (exportWidth - handWidth) / 2,
                y: (exportHeight - handHeight) / 2,
                width: handWidth,
                height: handHeight
            };
        }

        async drawHandBaseToContext(ctx, handRect, options = {}) {
            const hand = options.hand === 'right' ? 'right' : this.state.hand;
            const isRightHand = hand === 'right';
            const handSvg = this.svgCache['KC_lefthand.svg'] || await this.loadSVG('KC_lefthand.svg');
            if (!handSvg) return false;

            const modifiedSvg = this.tintSvgFill(handSvg, HAND_BASE_FILL);
            const blob = new Blob([modifiedSvg], { type: 'image/svg+xml' });
            const url = URL.createObjectURL(blob);
            const img = new Image();

            try {
                await new Promise((resolve, reject) => {
                    img.onload = resolve;
                    img.onerror = reject;
                    img.src = url;
                });

                ctx.save();
                if (isRightHand) {
                    ctx.translate(handRect.x + handRect.width, handRect.y);
                    ctx.scale(-1, 1);
                    ctx.drawImage(img, 0, 0, handRect.width, handRect.height);
                } else {
                    ctx.drawImage(img, handRect.x, handRect.y, handRect.width, handRect.height);
                }
                ctx.restore();
                return true;
            } finally {
                URL.revokeObjectURL(url);
            }
        }

        async drawResolvedNailsToContext(ctx, handRect, options = {}) {
            const hand = options.hand === 'right' ? 'right' : this.state.hand;
            const shapeSet = SHAPES.includes(options.shapeSet) ? options.shapeSet : this.state.shapeSet;
            const { transforms, isRightHand } = await this.resolveHandNailTransforms(
                handRect.width,
                handRect.height,
                { shapeSet, hand }
            );

            const drawOrder = [...transforms].sort((a, b) => a.zIndex - b.zIndex);
            for (const transform of drawOrder) {
                const nailCanvas = document.createElement('canvas');
                nailCanvas.width = HAND_NAIL_CANVAS_SIZE.width;
                nailCanvas.height = HAND_NAIL_CANVAS_SIZE.height;

                await this.renderNailToCanvas(
                    nailCanvas,
                    transform.finger,
                    this.state.nails[transform.finger],
                    'hand',
                    { mirror: isRightHand }
                );

                ctx.save();
                ctx.translate(handRect.x + transform.x, handRect.y + transform.y);
                ctx.rotate(transform.rotation * Math.PI / 180);
                ctx.drawImage(
                    nailCanvas,
                    -transform.width / 2,
                    -transform.height / 2,
                    transform.width,
                    transform.height
                );
                ctx.restore();
            }
        }

        async drawHandCompositionToContext(ctx, handRect, options = {}) {
            await this.drawHandBaseToContext(ctx, handRect, options);
            await this.drawResolvedNailsToContext(ctx, handRect, options);
        }

        async drawStageToCanvas(exportCanvas) {
            const exportWidth = exportCanvas.width;
            const exportHeight = exportCanvas.height;
            const ctx = exportCanvas.getContext('2d');
            const style = getComputedStyle(this.container);
            const bgColor = (style.getPropertyValue('--nb-bg-primary') || '#f8f9f6').trim();

            ctx.fillStyle = bgColor;
            ctx.fillRect(0, 0, exportWidth, exportHeight);

            const handRect = this.resolveStageHandRectForExport(exportWidth, exportHeight);
            await this.drawHandCompositionToContext(ctx, handRect, {
                hand: this.state.hand,
                shapeSet: this.state.shapeSet
            });
        }

        updateMobileSelectorLabels() {
            const handBtn = this.container.querySelector('[data-mobile-selector="hand"]');
            const fingerBtn = this.container.querySelector('[data-mobile-selector="finger"]');
            const shapeBtn = this.container.querySelector('[data-mobile-selector="shape"]');
            const patternBtn = this.container.querySelector('[data-mobile-selector="pattern"]');
            const charmsBtn = this.container.querySelector('[data-mobile-selector="charms"]');
            const design = this.state.nails[this.state.activeFinger];

            if (handBtn) handBtn.textContent = this.state.hand === 'right' ? 'Right' : 'Left';
            if (fingerBtn) fingerBtn.textContent = this.formatLabel(this.state.activeFinger);
            if (shapeBtn) shapeBtn.textContent = this.formatLabel(this.state.shapeSet);

            if (patternBtn) {
                if (design.pattern && design.pattern.enabled) {
                    patternBtn.textContent = this.patternLabels[design.pattern.id] || 'Pattern';
                } else {
                    patternBtn.textContent = 'Pattern';
                }
            }

            if (charmsBtn) {
                const charmCount = design.charms.length;
                charmsBtn.textContent = charmCount > 0 ? `Charms ${charmCount}` : 'Charms';
            }
        }

        formatLabel(value) {
            if (!value) return '';
            return value.charAt(0).toUpperCase() + value.slice(1);
        }

        async loadPatternPreview(patternId) {
            const container = this.container.querySelector(`#pattern-preview-${patternId}`);
            if (!container) return;

            const wrapper = document.createElement('span');
            wrapper.className = 'nail-builder__pattern-preview';
            container.replaceChildren(wrapper);

            const patternDef = this.getPatternDefinition(patternId);
            const previewImageUrl = this.normalizePatternAssetUrl(patternDef && patternDef.previewImage);
            if (previewImageUrl) {
                const img = document.createElement('img');
                img.src = previewImageUrl;
                img.alt = '';
                img.loading = 'lazy';
                img.decoding = 'async';
                img.setAttribute('aria-hidden', 'true');
                wrapper.appendChild(img);
                return;
            }

            if (patternId === 'frenchtip') {
                wrapper.innerHTML = this.createFrenchTipPreviewSvg('currentColor');
                const svg = wrapper.querySelector('svg');
                if (svg) {
                    svg.setAttribute('aria-hidden', 'true');
                    svg.style.width = '100%';
                    svg.style.height = '100%';
                }
                return;
            }

            const patternSource = this.resolvePatternSource(patternId, this.state.shapeSet, this.state.activeFinger);
            if (!patternSource) {
                wrapper.textContent = '•';
                return;
            }

            const svg = await this.loadSVG(patternSource);
            if (svg) {
                wrapper.innerHTML = this.prepareSvgPreviewMarkup(svg, 'currentColor');
                const svgEl = wrapper.querySelector('svg');
                if (svgEl) svgEl.style.cssText = 'width: 100%; height: 100%;';
            } else {
                wrapper.textContent = '•';
            }
        }

        prepareSvgPreviewMarkup(svgText, fillColor) {
            if (!svgText) return '';

            const parser = new DOMParser();
            const doc = parser.parseFromString(svgText, 'image/svg+xml');
            const svg = doc.querySelector('svg');
            if (!svg) return svgText;

            svg.querySelectorAll('script').forEach(node => node.remove());
            svg.querySelectorAll('style').forEach(node => node.remove());
            svg.querySelectorAll('[class]').forEach(node => node.removeAttribute('class'));
            svg.removeAttribute('width');
            svg.removeAttribute('height');
            svg.setAttribute('preserveAspectRatio', 'xMidYMid meet');

            svg.querySelectorAll('path, circle, ellipse, rect, polygon, polyline, line').forEach(node => {
                const style = String(node.getAttribute('style') || '').toLowerCase();
                const tagName = node.tagName.toLowerCase();
                const fillAttr = String(node.getAttribute('fill') || '').trim().toLowerCase();
                const strokeAttr = String(node.getAttribute('stroke') || '').trim().toLowerCase();

                const hasStrokeAttr = strokeAttr !== '' && strokeAttr !== 'none';
                const hasStrokeInStyle = /(^|;)\s*stroke\s*:\s*[^;]+/.test(style) && !/(^|;)\s*stroke\s*:\s*none\b/.test(style);
                const hasStroke = hasStrokeAttr || hasStrokeInStyle;

                const fillNoneAttr = fillAttr === 'none';
                const fillNoneInStyle = /(^|;)\s*fill\s*:\s*none\b/.test(style);
                const shouldStrokeOnly = tagName === 'line' || tagName === 'polyline' || hasStroke || fillNoneAttr || fillNoneInStyle;

                if (shouldStrokeOnly) {
                    node.setAttribute('fill', 'none');
                    node.setAttribute('stroke', fillColor);
                } else {
                    node.setAttribute('fill', fillColor);
                    if (hasStroke) node.setAttribute('stroke', fillColor);
                }

                node.removeAttribute('style');
            });

            return new XMLSerializer().serializeToString(svg);
        }

        createFrenchTipPreviewSvg(fillColor = 'currentColor') {
            return `
                <svg viewBox="0 0 60 60" aria-hidden="true" focusable="false" xmlns="http://www.w3.org/2000/svg">
                    <path d="M7 17 Q30 2 53 17 L53 27 Q30 13 7 27 Z" fill="${fillColor}"/>
                </svg>
            `;
        }

        getCanvasPoint(event, canvas) {
            const rect = canvas.getBoundingClientRect();
            return {
                x: (event.clientX - rect.left) / rect.width,
                y: (event.clientY - rect.top) / rect.height
            };
        }

        bindEditorCanvas(canvas) {
            if (!canvas || canvas.dataset.editorInputBound === 'true') return;

            canvas.style.touchAction = 'none';

            if (window.PointerEvent) {
                canvas.addEventListener('pointerdown', this.handleEditorMouseDown);
                canvas.addEventListener('pointermove', this.handleEditorMouseMove);
                canvas.addEventListener('pointerup', this.handleEditorMouseUp);
                canvas.addEventListener('pointercancel', this.handleEditorMouseUp);
                canvas.addEventListener('lostpointercapture', this.handleEditorMouseUp);
            } else {
                canvas.addEventListener('mousedown', this.handleEditorMouseDown);
                canvas.addEventListener('mousemove', this.handleEditorMouseMove);
                canvas.addEventListener('mouseup', this.handleEditorMouseUp);
                canvas.addEventListener('mouseleave', this.handleEditorMouseUp);
            }

            canvas.dataset.editorInputBound = 'true';
        }

        initEditor() {
            const canvas = this.container.querySelector('#editorCanvas');
            if (canvas) {
                this.bindEditorCanvas(canvas);
            }

            this.createGradientHandles();
        }

        createGradientHandles() {
            const container = this.container.querySelector('#editorCanvasContainer');
            if (!container) return;

            ['start', 'mid', 'end'].forEach(type => {
                const handle = document.createElement('div');
                handle.className = `nail-builder__gradient-handle ${type}`;
                handle.id = `gradient-handle-${type}`;
                handle.style.display = 'none';
                handle.dataset.handleType = type;

                handle.addEventListener('mousedown', (e) => {
                    e.stopPropagation();
                    this.editorState.draggingHandle = type;
                });

                container.appendChild(handle);
            });

            document.addEventListener('mousemove', this.handleGradientDrag);
            document.addEventListener('mouseup', () => {
                this.editorState.draggingHandle = null;
            });
        }

        // ============================================
        // HAND RENDERING
        // ============================================

        async renderHand() {
            const container = this.container.querySelector('#handSvgContainer');
            if (!container) return;

            const handSvg = await this.loadSVG('KC_lefthand.svg');
            if (handSvg) {
                let svg = this.tintSvgFill(handSvg, HAND_BASE_FILL);

                if (this.state.hand === 'right') {
                    svg = svg.replace('<svg ', '<svg style="transform: scaleX(-1);" ');
                }

                container.innerHTML = svg;
            }

            await this.renderNailsOnHand();
            this.queueHandOverlayRefresh([120, 320]);
        }

        async renderNailsOnHand() {
            const overlay = this.container.querySelector('#nailsOverlay');
            if (!overlay) return;
            const renderId = ++this.handOverlayRenderId;

            const overlayRect = overlay.getBoundingClientRect();
            let containerWidth = overlayRect.width;
            let containerHeight = overlayRect.height;

            if (!containerWidth || !containerHeight) {
                const fallbackElement = this.container.querySelector('#handWrapper')
                    || this.container.querySelector('#handStage')
                    || this.container.querySelector('#handSvgContainer');
                const fallbackRect = fallbackElement ? fallbackElement.getBoundingClientRect() : null;

                if (fallbackRect && fallbackRect.width > 0 && fallbackRect.height > 0) {
                    containerWidth = fallbackRect.width;
                    containerHeight = fallbackRect.height;
                }
            }

            if (!containerWidth || !containerHeight) return;

            const { transforms } = await this.resolveHandNailTransforms(
                containerWidth,
                containerHeight,
                {
                    hand: this.state.hand,
                    shapeSet: this.state.shapeSet
                }
            );
            if (renderId !== this.handOverlayRenderId) return;

            const nextOverlay = document.createDocumentFragment();

            for (const transform of transforms) {
                if (renderId !== this.handOverlayRenderId) return;

                const finger = transform.finger;

                const nailEl = document.createElement('div');
                nailEl.className = `nail-builder__nail-on-hand ${finger === this.state.activeFinger ? 'active' : ''}`;
                nailEl.dataset.finger = finger;
                nailEl.onclick = () => this.selectFinger(finger);

                const canvas = document.createElement('canvas');
                canvas.width = HAND_NAIL_CANVAS_SIZE.width;
                canvas.height = HAND_NAIL_CANVAS_SIZE.height;
                canvas.style.width = '100%';
                canvas.style.height = '100%';
                canvas.style.display = 'block';

                await this.renderNailToCanvas(canvas, finger, this.state.nails[finger], 'hand', {
                    mirror: transform.mirror,
                    highlight: finger === this.state.activeFinger
                });

                const left = (transform.x / containerWidth) * 100;
                const top = (transform.y / containerHeight) * 100;
                const width = (transform.width / containerWidth) * 100;
                const height = (transform.height / containerHeight) * 100;

                nailEl.style.cssText = `
                    left: ${left}%;
                    top: ${top}%;
                    width: ${width}%;
                    height: ${height}%;
                    z-index: ${transform.zIndex};
                    transform: translate(-50%, -50%) rotate(${transform.rotation}deg);
                `;

                nailEl.appendChild(canvas);
                nextOverlay.appendChild(nailEl);
            }

            if (renderId !== this.handOverlayRenderId) return;
            overlay.replaceChildren(nextOverlay);
        }

        // ============================================
        // NAIL RENDERING
        // ============================================

        async renderNailToCanvas(canvas, finger, design, renderMode = 'editor', options = {}) {
            const targetCtx = canvas.getContext('2d');
            if (!targetCtx) return;

            const w = canvas.width;
            const h = canvas.height;
            const renderCanvas = document.createElement('canvas');
            renderCanvas.width = w;
            renderCanvas.height = h;
            const ctx = renderCanvas.getContext('2d');
            if (!ctx) return;

            ctx.imageSmoothingEnabled = true;
            ctx.imageSmoothingQuality = 'high';
            targetCtx.imageSmoothingEnabled = true;
            targetCtx.imageSmoothingQuality = 'high';

            ctx.clearRect(0, 0, w, h);

            const isThumb = finger === 'thumb';
            const useTopDownForThumb = isThumb && (renderMode === 'editor' || renderMode === 'strip');
            const nailFile = useTopDownForThumb
                ? NAIL_FILES[this.state.shapeSet]['pinky']
                : NAIL_FILES[this.state.shapeSet][finger];
            const pathData = this.svgPathCache[nailFile];

            if (!pathData) {
                await this.loadSVG(nailFile);
            }

            const svgText = this.svgCache[nailFile];
            if (!svgText) return;

            const vbMatch = svgText.match(/viewBox="([^"]+)"/);
            if (!vbMatch) return;

            const [vbX, vbY, vbW, vbH] = vbMatch[1].split(' ').map(Number);
            const pathMatch = svgText.match(/\sd="([^"]+)"/);
            if (!pathMatch) return;

            const needsMirror = renderMode === 'hand' && options.mirror;

            ctx.save();

            if (needsMirror) {
                ctx.translate(w, 0);
                ctx.scale(-1, 1);
            }

            const scaleX = w / vbW;
            const scaleY = h / vbH;
            const scale = Math.min(scaleX, scaleY) * 0.9;

            ctx.translate(w / 2, h / 2);
            ctx.scale(scale, scale);
            ctx.translate(-vbW / 2, -vbH / 2);

            const pathString = pathMatch[1];
            const path = new Path2D(pathString);
            const patternContext = {
                finger,
                renderMode,
                shapeSet: this.state.shapeSet,
                nailFile,
                path,
                pathString,
                nailWidth: vbW,
                nailHeight: vbH
            };
            ctx.clip(path);

            this.drawBase(ctx, design.base, vbW, vbH, design, patternContext);

            if (design.pattern && design.pattern.enabled && design.base.kind !== 'split') {
                await this.drawPattern(ctx, design.pattern, vbW, vbH, patternContext);
            }

            const charmOptions = {
                isThumbInHandMode: isThumb && renderMode === 'hand',
                shapeSet: this.state.shapeSet
            };
            for (const charm of design.charms) {
                this.drawCharm(ctx, charm, vbW, vbH, charmOptions);
            }

            ctx.restore();
            targetCtx.clearRect(0, 0, w, h);
            targetCtx.drawImage(renderCanvas, 0, 0);
        }

        drawBase(ctx, base, w, h, fullDesign = null, patternContext = null) {
            if (!base) return;

            ctx.save();

            if (base.kind === 'split') {
                const splitY = h * base.splitT;

                ctx.save();
                ctx.beginPath();
                ctx.rect(0, 0, w, splitY);
                ctx.clip();
                this.drawBase(ctx, base.top, w, h, null, patternContext);
                ctx.restore();

                ctx.save();
                ctx.beginPath();
                ctx.rect(0, splitY, w, h - splitY);
                ctx.clip();
                this.drawBase(ctx, base.bottom, w, h, null, patternContext);

                if (fullDesign && fullDesign.pattern && fullDesign.pattern.enabled) {
                    this.drawPatternSync(ctx, fullDesign.pattern, w, h, patternContext);
                }
                ctx.restore();

            } else if (base.kind === 'solid') {
                ctx.globalAlpha = base.opacity;
                ctx.fillStyle = base.color;
                ctx.fillRect(0, 0, w, h);

            } else if (base.kind === 'nude') {
                const nudePreset = NUDE_PRESETS.find(n => n.id === base.nudeId) || NUDE_PRESETS[0];
                ctx.globalAlpha = base.opacity;
                ctx.fillStyle = nudePreset.color;
                ctx.fillRect(0, 0, w, h);

            } else if (base.kind === 'metallic') {
                const grad = ctx.createLinearGradient(0, 0, w, h);
                const intensity = base.intensity;
                grad.addColorStop(0, `rgba(192, 192, 192, ${intensity})`);
                grad.addColorStop(0.3, `rgba(255, 255, 255, ${intensity})`);
                grad.addColorStop(0.5, `rgba(192, 192, 192, ${intensity})`);
                grad.addColorStop(0.7, `rgba(128, 128, 128, ${intensity})`);
                grad.addColorStop(1, `rgba(192, 192, 192, ${intensity})`);
                ctx.fillStyle = grad;
                ctx.fillRect(0, 0, w, h);

            } else if (base.kind === 'gradient') {
                const g = base.gradient;
                const grad = ctx.createLinearGradient(
                    g.x1 * w, g.y1 * h,
                    g.x2 * w, g.y2 * h
                );

                g.stops.forEach(stop => {
                    const rgba = this.hexToRgba(stop.color, stop.opacity);
                    grad.addColorStop(stop.t, rgba);
                });

                ctx.fillStyle = grad;
                ctx.fillRect(0, 0, w, h);
            }

            ctx.restore();
        }

        async drawPattern(ctx, pattern, w, h, patternContext = null) {
            if (this.isFrenchTipPattern(pattern)) {
                this.ensureFrenchTipPatternFields(pattern);
                this.drawProceduralFrenchTip(ctx, pattern, w, h, patternContext);
                return;
            }

            const patternSource = this.resolvePatternSource(
                pattern.id,
                this.state.shapeSet,
                patternContext && patternContext.finger ? patternContext.finger : this.state.activeFinger
            );
            if (!patternSource) return;

            let svgText = this.svgCache[patternSource];
            if (!svgText) {
                svgText = await this.loadSVG(patternSource);
            }
            if (!svgText) return;

            ctx.save();
            ctx.globalAlpha = pattern.opacity;

            const tintedSvg = svgText.replace(/fill:[^;"]*/g, `fill:${pattern.tint}`);
            const blob = new Blob([tintedSvg], { type: 'image/svg+xml' });
            const url = URL.createObjectURL(blob);

            const img = new Image();
            await new Promise((resolve, reject) => {
                img.onload = resolve;
                img.onerror = reject;
                img.src = url;
            });

            ctx.drawImage(img, 0, 0, w, h);
            URL.revokeObjectURL(url);

            ctx.restore();
        }

        drawPatternSync(ctx, pattern, w, h, patternContext = null) {
            if (this.isFrenchTipPattern(pattern)) {
                this.ensureFrenchTipPatternFields(pattern);
                this.drawProceduralFrenchTip(ctx, pattern, w, h, patternContext);
                return;
            }

            const patternSource = this.resolvePatternSource(
                pattern.id,
                this.state.shapeSet,
                patternContext && patternContext.finger ? patternContext.finger : this.state.activeFinger
            );
            if (!patternSource) return;

            const svgText = this.svgCache[patternSource];
            if (!svgText) return;

            ctx.save();
            ctx.globalAlpha = pattern.opacity;

            const parser = new DOMParser();
            const tintedSvg = svgText.replace(/fill:[^;"]*/g, `fill:${pattern.tint}`);
            const doc = parser.parseFromString(tintedSvg, 'image/svg+xml');
            const svg = doc.querySelector('svg');
            const vbMatch = svg ? svg.getAttribute('viewBox') : null;

            if (vbMatch) {
                const [, , svgW, svgH] = vbMatch.split(' ').map(Number);
                const scaleX = w / svgW;
                const scaleY = h / svgH;

                ctx.scale(scaleX, scaleY);

                const paths = doc.querySelectorAll('path');
                paths.forEach(pathEl => {
                    const d = pathEl.getAttribute('d');
                    if (d) {
                        const path = new Path2D(d);
                        ctx.fillStyle = pattern.tint;
                        ctx.fill(path);
                    }
                });
            }

            ctx.restore();
        }

        isFrenchTipPattern(pattern) {
            return !!pattern && pattern.id === 'frenchtip';
        }

        ensureFrenchTipPatternFields(pattern) {
            if (!this.isFrenchTipPattern(pattern)) return;
            pattern.tipDepth = this.normalizeFrenchTipDepth(pattern.tipDepth);
            pattern.smileCurvature = this.normalizeFrenchTipCurvature(pattern.smileCurvature);
        }

        smoothContour(points, radius = 2) {
            if (!Array.isArray(points) || points.length < 3 || radius <= 0) return points;

            return points.map((point, index) => {
                let sum = 0;
                let weightSum = 0;

                for (let offset = -radius; offset <= radius; offset++) {
                    const targetIndex = index + offset;
                    if (targetIndex < 0 || targetIndex >= points.length) continue;
                    const weight = radius + 1 - Math.abs(offset);
                    sum += points[targetIndex].y * weight;
                    weightSum += weight;
                }

                return {
                    x: point.x,
                    y: weightSum > 0 ? sum / weightSum : point.y
                };
            });
        }

        getFrenchTipContours(patternContext) {
            if (!patternContext || !patternContext.pathString) return null;

            const nailWidth = Number(patternContext.nailWidth);
            const nailHeight = Number(patternContext.nailHeight);
            if (!Number.isFinite(nailWidth) || !Number.isFinite(nailHeight) || nailWidth <= 0 || nailHeight <= 0) {
                return null;
            }

            const nailFile = patternContext.nailFile || patternContext.pathString;
            const cacheKey = `${nailFile}:${Math.round(nailWidth)}x${Math.round(nailHeight)}`;
            if (this.frenchTipContourCache[cacheKey]) {
                return this.frenchTipContourCache[cacheKey];
            }

            const sampleWidth = Math.max(80, Math.round(nailWidth * 3));
            const sampleHeight = Math.max(220, Math.round(nailHeight * 3));

            const probe = document.createElement('canvas');
            probe.width = sampleWidth;
            probe.height = sampleHeight;
            const probeCtx = probe.getContext('2d');
            if (!probeCtx) return null;

            const shapePath = new Path2D(patternContext.pathString);
            probeCtx.save();
            probeCtx.scale(sampleWidth / nailWidth, sampleHeight / nailHeight);
            probeCtx.fillStyle = '#000';
            probeCtx.fill(shapePath);
            probeCtx.restore();

            const imageData = probeCtx.getImageData(0, 0, sampleWidth, sampleHeight).data;
            const alphaAt = (x, y) => imageData[(y * sampleWidth + x) * 4 + 3];

            const outer = [];
            const bottom = [];

            for (let x = 0; x < sampleWidth; x++) {
                let topY = -1;
                let bottomY = -1;

                for (let y = 0; y < sampleHeight; y++) {
                    if (alphaAt(x, y) > 0) {
                        topY = y;
                        break;
                    }
                }

                if (topY === -1) continue;

                for (let y = sampleHeight - 1; y >= topY; y--) {
                    if (alphaAt(x, y) > 0) {
                        bottomY = y;
                        break;
                    }
                }

                const xPos = ((x + 0.5) / sampleWidth) * nailWidth;
                outer.push({
                    x: xPos,
                    y: ((topY + 0.5) / sampleHeight) * nailHeight
                });
                bottom.push({
                    x: xPos,
                    y: ((bottomY + 0.5) / sampleHeight) * nailHeight
                });
            }

            if (outer.length < 6) return null;

            const contours = {
                outer: this.smoothContour(outer, 2),
                bottom: this.smoothContour(bottom, 1)
            };
            this.frenchTipContourCache[cacheKey] = contours;
            return contours;
        }

        buildFrenchTipCurves(contours, pattern, nailHeight, patternContext = null) {
            const outer = contours.outer || [];
            const bottom = contours.bottom || [];
            if (outer.length < 2 || bottom.length < 2) return null;

            const tipDepthPx = (this.normalizeFrenchTipDepth(pattern.tipDepth) / 100) * nailHeight;
            const smileNormalized = this.normalizeFrenchTipCurvature(pattern.smileCurvature) / 100;
            const minGap = Math.max(1, nailHeight * 0.0055);
            const isThumbOnHand = !!patternContext
                && patternContext.finger === 'thumb'
                && patternContext.renderMode === 'hand';
            const seamGuide = [];
            const inner = [];
            const depthWeights = [];
            const pointMinGaps = [];
            let bandStart = 0;
            let bandEnd = outer.length - 1;
            const outerMinY = outer.reduce((min, point) => Math.min(min, point.y), Infinity);
            const outerMaxY = outer.reduce((max, point) => Math.max(max, point.y), -Infinity);
            const outerSpanY = Math.max(0.0001, outerMaxY - outerMinY);
            const thumbCentroid = isThumbOnHand
                ? outer.reduce((acc, point, index) => {
                    const bottomPoint = bottom[index] || point;
                    acc.x += (point.x + bottomPoint.x) * 0.5;
                    acc.y += (point.y + bottomPoint.y) * 0.5;
                    return acc;
                }, { x: 0, y: 0 })
                : null;

            if (thumbCentroid) {
                thumbCentroid.x /= outer.length;
                thumbCentroid.y /= outer.length;
            }

            for (let index = 0; index < outer.length; index++) {
                const outerPoint = outer[index];
                const bottomPoint = bottom[index] || bottom[bottom.length - 1];
                const t = outer.length === 1 ? 0.5 : index / (outer.length - 1);
                const centerWeight = 1 - Math.pow(Math.abs(2 * t - 1), 1.55);

                let depthWeight = 1;
                let smileWeight = centerWeight;
                if (isThumbOnHand) {
                    // Side-turned thumb nails visually expose less full-width tip area.
                    // Keep the cap apex-focused, but allow more visible band area than front view.
                    const thumbStart = 0.74;
                    const thumbEnd = 0.985;
                    const thumbT = this.clamp((t - thumbStart) / (thumbEnd - thumbStart), 0, 1);
                    const smoothThumbT = thumbT * thumbT * (3 - 2 * thumbT);
                    const tipPeak = Math.max(0, 1 - Math.pow(Math.abs((t - 0.84) / 0.2), 2));
                    const yNorm = (outerPoint.y - outerMinY) / outerSpanY;
                    const verticalT = this.clamp((0.52 - yNorm) / 0.52, 0, 1);
                    const verticalWeight = verticalT * verticalT * (3 - 2 * verticalT);
                    depthWeight = this.clamp(Math.pow(smoothThumbT, 1.1) * 1.9, 0, 1);
                    smileWeight = tipPeak * (0.45 + 0.55 * smoothThumbT);
                    depthWeight *= verticalWeight;
                    smileWeight *= verticalWeight;
                }

                const pointMinGap = isThumbOnHand
                    ? minGap * Math.max(0.02, depthWeight)
                    : minGap;
                const smileScale = isThumbOnHand ? 1.25 : 0.9;
                const maxDistance = isThumbOnHand && thumbCentroid
                    ? Math.max(
                        pointMinGap,
                        Math.hypot(thumbCentroid.x - outerPoint.x, thumbCentroid.y - outerPoint.y) * 0.82
                    )
                    : Math.max(
                        pointMinGap,
                        Math.hypot((bottomPoint.x - outerPoint.x), (bottomPoint.y - outerPoint.y)) - pointMinGap
                    );

                if (isThumbOnHand && thumbCentroid) {
                    const inwardVecX = thumbCentroid.x - outerPoint.x;
                    const inwardVecY = thumbCentroid.y - outerPoint.y;
                    const inwardLen = Math.hypot(inwardVecX, inwardVecY) || 1;
                    const inwardX = inwardVecX / inwardLen;
                    const inwardY = inwardVecY / inwardLen;

                    const seamDistance = this.clamp(tipDepthPx * depthWeight, pointMinGap, maxDistance);
                    const curvedDistance = this.clamp(
                        seamDistance + (tipDepthPx * smileScale * smileNormalized * smileWeight),
                        pointMinGap,
                        maxDistance
                    );

                    seamGuide.push({
                        x: outerPoint.x + inwardX * seamDistance,
                        y: outerPoint.y + inwardY * seamDistance
                    });

                    inner.push({
                        x: outerPoint.x + inwardX * curvedDistance,
                        y: outerPoint.y + inwardY * curvedDistance
                    });
                } else {
                    const seamY = outerPoint.y + tipDepthPx * depthWeight;
                    const curvedTargetY = seamY + (tipDepthPx * smileScale * smileNormalized * smileWeight);
                    const maxY = Math.max(
                        outerPoint.y + pointMinGap,
                        (bottomPoint ? bottomPoint.y : nailHeight) - pointMinGap
                    );

                    seamGuide.push({
                        x: outerPoint.x,
                        y: this.clamp(seamY, outerPoint.y + pointMinGap, maxY)
                    });

                    inner.push({
                        x: outerPoint.x,
                        y: this.clamp(curvedTargetY, outerPoint.y + pointMinGap, maxY)
                    });
                }
                depthWeights.push(depthWeight);
                pointMinGaps.push(pointMinGap);
            }

            if (isThumbOnHand && thumbCentroid) {
                const weightedStartIndex = depthWeights.findIndex(weight => weight > 0.12);
                const geometricStartIndex = Math.floor(inner.length * 0.86);
                const startIndex = weightedStartIndex === -1
                    ? geometricStartIndex
                    : Math.max(weightedStartIndex, geometricStartIndex);
                if (startIndex !== -1 && startIndex < inner.length - 2) {
                    const endIndex = inner.length - 1;
                    bandStart = startIndex;
                    bandEnd = endIndex;
                    const startOuter = outer[startIndex];
                    const startMinGap = pointMinGaps[startIndex] ?? minGap;
                    const startInwardXRaw = thumbCentroid.x - startOuter.x;
                    const startInwardYRaw = thumbCentroid.y - startOuter.y;
                    const startInwardLen = Math.hypot(startInwardXRaw, startInwardYRaw) || 1;
                    const startPoint = {
                        x: startOuter.x + (startInwardXRaw / startInwardLen) * startMinGap,
                        y: startOuter.y + (startInwardYRaw / startInwardLen) * startMinGap
                    };
                    const endOuter = outer[endIndex];
                    const endMinGap = pointMinGaps[endIndex] ?? minGap;
                    const endInwardXRaw = thumbCentroid.x - endOuter.x;
                    const endInwardYRaw = thumbCentroid.y - endOuter.y;
                    const endInwardLen = Math.hypot(endInwardXRaw, endInwardYRaw) || 1;
                    const endMaxDistance = Math.max(
                        endMinGap,
                        Math.hypot(thumbCentroid.x - endOuter.x, thumbCentroid.y - endOuter.y) * 0.82
                    );
                    const endDepth = this.clamp(
                        tipDepthPx * 0.3,
                        endMinGap * 1.4,
                        endMaxDistance * 0.82
                    );
                    const endPoint = {
                        x: endOuter.x + (endInwardXRaw / endInwardLen) * endDepth,
                        y: endOuter.y + (endInwardYRaw / endInwardLen) * endDepth
                    };
                    const midIndex = Math.floor((startIndex + endIndex) / 2);
                    const midOuter = outer[midIndex];
                    const inwardVecX = thumbCentroid.x - midOuter.x;
                    const inwardVecY = thumbCentroid.y - midOuter.y;
                    const inwardLen = Math.hypot(inwardVecX, inwardVecY) || 1;
                    const inwardX = inwardVecX / inwardLen;
                    const inwardY = inwardVecY / inwardLen;
                    const controlDepth = this.clamp(
                        tipDepthPx * (0.28 + Math.max(0, smileNormalized) * 0.35),
                        tipDepthPx * 0.18,
                        nailHeight * 0.13
                    );
                    const controlPoint = {
                        x: (startPoint.x + endPoint.x) * 0.5 + inwardX * controlDepth,
                        y: (startPoint.y + endPoint.y) * 0.5 + inwardY * controlDepth
                    };

                    const shapedInner = inner.map((point, index) => ({ ...point }));

                    for (let index = 0; index < startIndex; index++) {
                        const outerPoint = outer[index];
                        const pointMinGap = pointMinGaps[index] ?? minGap;
                        const preInwardX = thumbCentroid.x - outerPoint.x;
                        const preInwardY = thumbCentroid.y - outerPoint.y;
                        const preInwardLen = Math.hypot(preInwardX, preInwardY) || 1;
                        shapedInner[index] = {
                            x: outerPoint.x + (preInwardX / preInwardLen) * pointMinGap,
                            y: outerPoint.y + (preInwardY / preInwardLen) * pointMinGap
                        };
                    }

                    for (let index = startIndex; index <= endIndex; index++) {
                        const outerPoint = outer[index];
                        const bottomPoint = bottom[index] || bottom[bottom.length - 1];
                        const pointMinGap = pointMinGaps[index] ?? minGap;
                        const maxDistance = isThumbOnHand && thumbCentroid
                            ? Math.max(
                                pointMinGap,
                                Math.hypot(thumbCentroid.x - outerPoint.x, thumbCentroid.y - outerPoint.y) * 0.82
                            )
                            : Math.max(
                                pointMinGap,
                                Math.hypot((bottomPoint.x - outerPoint.x), (bottomPoint.y - outerPoint.y)) - pointMinGap
                            );
                        const u = (index - startIndex) / Math.max(1, endIndex - startIndex);
                        const oneMinusU = 1 - u;
                        const qx = oneMinusU * oneMinusU * startPoint.x
                            + 2 * oneMinusU * u * controlPoint.x
                            + u * u * endPoint.x;
                        const qy = oneMinusU * oneMinusU * startPoint.y
                            + 2 * oneMinusU * u * controlPoint.y
                            + u * u * endPoint.y;

                        let offsetX = qx - outerPoint.x;
                        let offsetY = qy - outerPoint.y;
                        let distance = Math.hypot(offsetX, offsetY);

                        if (distance < 0.0001) {
                            offsetX = inwardX;
                            offsetY = inwardY;
                            distance = 1;
                        }

                        const clampedDistance = this.clamp(distance, pointMinGap, maxDistance);
                        const scale = clampedDistance / distance;
                        shapedInner[index] = {
                            x: outerPoint.x + offsetX * scale,
                            y: outerPoint.y + offsetY * scale
                        };
                    }

                    return {
                        outer,
                        seamGuide,
                        inner: shapedInner,
                        bandStart,
                        bandEnd
                    };
                }
            }

            const smoothedInnerRaw = this.smoothContour(inner, 2);
            const smoothedInner = smoothedInnerRaw.map((point, index) => {
                const outerPoint = outer[index];
                const rawPoint = inner[index];
                const bottomPoint = bottom[index] || bottom[bottom.length - 1];
                const pointMinGap = pointMinGaps[index] ?? minGap;
                const blend = isThumbOnHand ? this.clamp(depthWeights[index] || 0, 0, 1) : 1;
                const maxDistance = isThumbOnHand && thumbCentroid
                    ? Math.max(
                        pointMinGap,
                        Math.hypot(thumbCentroid.x - outerPoint.x, thumbCentroid.y - outerPoint.y) * 0.82
                    )
                    : Math.max(
                        pointMinGap,
                        Math.hypot((bottomPoint.x - outerPoint.x), (bottomPoint.y - outerPoint.y)) - pointMinGap
                    );

                if (isThumbOnHand && thumbCentroid) {
                    const inwardVecX = thumbCentroid.x - outerPoint.x;
                    const inwardVecY = thumbCentroid.y - outerPoint.y;
                    const inwardLen = Math.hypot(inwardVecX, inwardVecY) || 1;
                    const inwardX = inwardVecX / inwardLen;
                    const inwardY = inwardVecY / inwardLen;
                    const rawDistance = Math.hypot(rawPoint.x - outerPoint.x, rawPoint.y - outerPoint.y);
                    const smoothDistance = Math.hypot(point.x - outerPoint.x, point.y - outerPoint.y);
                    const blendedDistance = rawDistance + (smoothDistance - rawDistance) * blend;
                    const clampedDistance = this.clamp(blendedDistance, pointMinGap, maxDistance);
                    return {
                        x: outerPoint.x + inwardX * clampedDistance,
                        y: outerPoint.y + inwardY * clampedDistance
                    };
                }

                const maxY = Math.max(
                    outerPoint.y + pointMinGap,
                    (bottomPoint ? bottomPoint.y : nailHeight) - pointMinGap
                );
                const blendedY = rawPoint.y + (point.y - rawPoint.y) * blend;
                return {
                    x: point.x,
                    y: this.clamp(blendedY, outerPoint.y + pointMinGap, maxY)
                };
            });

            return {
                outer,
                seamGuide,
                inner: smoothedInner,
                bandStart,
                bandEnd
            };
        }

        drawContourLine(ctx, points) {
            if (!Array.isArray(points) || points.length < 2) return;

            ctx.beginPath();
            ctx.moveTo(points[0].x, points[0].y);
            for (let index = 1; index < points.length; index++) {
                ctx.lineTo(points[index].x, points[index].y);
            }
            ctx.stroke();
        }

        drawThumbHandFrenchTipCap(ctx, pattern, contours, nailHeight) {
            const outer = contours && Array.isArray(contours.outer) ? contours.outer : [];
            const bottom = contours && Array.isArray(contours.bottom) ? contours.bottom : [];
            if (outer.length < 8 || bottom.length < 8) return false;

            let tipIndex = 0;
            for (let index = 1; index < outer.length; index++) {
                if (outer[index].x > outer[tipIndex].x) {
                    tipIndex = index;
                }
            }

            const tipTop = outer[tipIndex];
            const tipBottom = bottom[tipIndex] || tipTop;
            const tipMid = {
                x: (tipTop.x + tipBottom.x) * 0.5,
                y: (tipTop.y + tipBottom.y) * 0.5
            };

            const centroid = outer.reduce((acc, point, index) => {
                const bottomPoint = bottom[index] || point;
                acc.x += (point.x + bottomPoint.x) * 0.5;
                acc.y += (point.y + bottomPoint.y) * 0.5;
                return acc;
            }, { x: 0, y: 0 });
            centroid.x /= outer.length;
            centroid.y /= outer.length;

            let inwardX = centroid.x - tipMid.x;
            let inwardY = centroid.y - tipMid.y;
            let inwardLength = Math.hypot(inwardX, inwardY);
            if (inwardLength < 0.0001) {
                inwardX = -1;
                inwardY = 0;
                inwardLength = 1;
            }
            inwardX /= inwardLength;
            inwardY /= inwardLength;

            const smileNormalized = this.normalizeFrenchTipCurvature(pattern.smileCurvature) / 100;
            const tipDepth = this.normalizeFrenchTipDepth(pattern.tipDepth);
            const depthNormalized = this.clamp(
                (tipDepth - FRENCH_TIP_MIN_DEPTH) / Math.max(1, FRENCH_TIP_MAX_DEPTH - FRENCH_TIP_MIN_DEPTH),
                0,
                1
            );

            const tipDepthPx = (tipDepth / 100) * nailHeight;
            const deepArcTarget = this.clamp(
                tipDepthPx * (2.9 + depthNormalized * 0.7 + smileNormalized * 0.2),
                nailHeight * 0.28,
                nailHeight * 0.82
            );
            const shallowArcTarget = this.clamp(
                tipDepthPx * (0.56 + (1 - depthNormalized) * 0.16 - smileNormalized * 0.06),
                nailHeight * 0.05,
                deepArcTarget * 0.7
            );

            const walkContourByDistance = (points, startIndex, targetDistance) => {
                let index = startIndex;
                let traveled = 0;
                while (index > 0 && traveled < targetDistance) {
                    const current = points[index];
                    const previous = points[index - 1];
                    traveled += Math.hypot(current.x - previous.x, current.y - previous.y);
                    index -= 1;
                }
                return {
                    index,
                    traveled
                };
            };

            const lowerOuterWalk = walkContourByDistance(outer, tipIndex, deepArcTarget);
            const upperBottomWalk = walkContourByDistance(bottom, tipIndex, shallowArcTarget);

            let lowerOuterIndex = this.clamp(lowerOuterWalk.index, 0, tipIndex - 2);
            let upperBottomIndex = this.clamp(upperBottomWalk.index, 1, tipIndex - 1);
            const minimumSpan = Math.max(6, Math.round(outer.length * 0.11));

            if (upperBottomIndex - lowerOuterIndex < minimumSpan) {
                lowerOuterIndex = Math.max(0, upperBottomIndex - minimumSpan);
            }

            if (upperBottomIndex <= lowerOuterIndex || tipIndex <= lowerOuterIndex + 1) {
                return false;
            }

            const lowerSeamPoint = outer[lowerOuterIndex];
            const upperSeamPoint = bottom[upperBottomIndex] || outer[upperBottomIndex];
            const seamMid = {
                x: (upperSeamPoint.x + lowerSeamPoint.x) * 0.5,
                y: (upperSeamPoint.y + lowerSeamPoint.y) * 0.5
            };
            const smileFactor = this.clamp((smileNormalized + 1) * 0.5, 0, 1);
            const tipCurveAmount = this.clamp(0.08 + smileFactor * 0.16, 0.05, 0.3);
            const inwardInset = this.clamp(
                nailHeight * (0.028 * (1 - depthNormalized) - 0.012 * smileFactor),
                -nailHeight * 0.012,
                nailHeight * 0.034
            );
            const toTipX = tipMid.x - seamMid.x;
            const toTipY = tipMid.y - seamMid.y;
            const seamControl = {
                x: seamMid.x + toTipX * tipCurveAmount + inwardX * inwardInset,
                y: seamMid.y + toTipY * tipCurveAmount + inwardY * inwardInset
            };

            if (this.debugMode) {
                this.lastThumbCapDebug = {
                    tipDepth,
                    depthNormalized,
                    tipDepthPx,
                    shallowArcTarget,
                    deepArcTarget,
                    upperArcDistance: upperBottomWalk.traveled,
                    lowerArcDistance: lowerOuterWalk.traveled,
                    tipIndex,
                    lowerOuterIndex,
                    upperBottomIndex,
                    upperSeamPoint,
                    lowerSeamPoint,
                    seamControl
                };
            }

            const bandPath = new Path2D();
            bandPath.moveTo(lowerSeamPoint.x, lowerSeamPoint.y);
            for (let index = lowerOuterIndex + 1; index <= tipIndex; index++) {
                bandPath.lineTo(outer[index].x, outer[index].y);
            }
            for (let index = tipIndex; index >= upperBottomIndex; index--) {
                const point = bottom[index] || outer[index];
                bandPath.lineTo(point.x, point.y);
            }
            bandPath.quadraticCurveTo(seamControl.x, seamControl.y, lowerSeamPoint.x, lowerSeamPoint.y);
            bandPath.closePath();

            ctx.save();
            ctx.globalAlpha = pattern.opacity;
            ctx.fillStyle = pattern.tint;
            ctx.fill(bandPath);
            ctx.restore();

            if (!this.debugMode) return true;

            const seamCurve = [];
            const seamSteps = 20;
            for (let step = 0; step <= seamSteps; step++) {
                const t = step / seamSteps;
                const oneMinusT = 1 - t;
                seamCurve.push({
                    x: oneMinusT * oneMinusT * lowerSeamPoint.x
                        + 2 * oneMinusT * t * seamControl.x
                        + t * t * upperSeamPoint.x,
                    y: oneMinusT * oneMinusT * lowerSeamPoint.y
                        + 2 * oneMinusT * t * seamControl.y
                        + t * t * upperSeamPoint.y
                });
            }

            ctx.save();
            ctx.fillStyle = 'rgba(14, 165, 233, 0.28)';
            ctx.fill(bandPath);

            ctx.lineWidth = Math.max(2, nailHeight * 0.011);
            ctx.setLineDash([Math.max(4, nailHeight * 0.018), Math.max(3, nailHeight * 0.012)]);
            ctx.strokeStyle = 'rgba(234, 179, 8, 0.95)';
            this.drawContourLine(ctx, [lowerSeamPoint, upperSeamPoint]);

            ctx.setLineDash([]);
            ctx.strokeStyle = 'rgba(244, 63, 94, 0.95)';
            this.drawContourLine(ctx, outer);

            ctx.strokeStyle = 'rgba(16, 185, 129, 0.95)';
            this.drawContourLine(ctx, seamCurve);

            ctx.strokeStyle = 'rgba(14, 165, 233, 0.95)';
            ctx.stroke(bandPath);
            ctx.restore();

            return true;
        }

        drawProceduralFrenchTip(ctx, pattern, w, h, patternContext = null) {
            const contours = this.getFrenchTipContours(patternContext);
            if (!contours) return;

            const isThumbOnHand = !!patternContext
                && patternContext.finger === 'thumb'
                && patternContext.renderMode === 'hand';
            if (isThumbOnHand && this.drawThumbHandFrenchTipCap(ctx, pattern, contours, h)) {
                return;
            }

            const curves = this.buildFrenchTipCurves(contours, pattern, h, patternContext);
            if (!curves) return;

            const bandPath = new Path2D();
            const { outer, seamGuide, inner } = curves;
            const startIndex = this.clamp(
                Number.isFinite(curves.bandStart) ? curves.bandStart : 0,
                0,
                outer.length - 1
            );
            const endIndex = this.clamp(
                Number.isFinite(curves.bandEnd) ? curves.bandEnd : outer.length - 1,
                startIndex + 1,
                outer.length - 1
            );
            const outerBand = outer.slice(startIndex, endIndex + 1);
            const innerBand = inner.slice(startIndex, endIndex + 1);
            if (outerBand.length < 2 || innerBand.length < 2) return;

            bandPath.moveTo(outerBand[0].x, outerBand[0].y);
            for (let index = 1; index < outerBand.length; index++) {
                bandPath.lineTo(outerBand[index].x, outerBand[index].y);
            }
            for (let index = innerBand.length - 1; index >= 0; index--) {
                bandPath.lineTo(innerBand[index].x, innerBand[index].y);
            }
            bandPath.closePath();

            ctx.save();
            ctx.globalAlpha = pattern.opacity;
            ctx.fillStyle = pattern.tint;
            ctx.fill(bandPath);
            ctx.restore();

            if (!this.debugMode) return;

            ctx.save();
            ctx.fillStyle = 'rgba(14, 165, 233, 0.28)';
            ctx.fill(bandPath);

            ctx.lineWidth = Math.max(2, h * 0.011);
            ctx.setLineDash([Math.max(4, h * 0.018), Math.max(3, h * 0.012)]);
            ctx.strokeStyle = 'rgba(234, 179, 8, 0.95)';
            this.drawContourLine(ctx, seamGuide);

            ctx.setLineDash([]);
            ctx.strokeStyle = 'rgba(244, 63, 94, 0.95)';
            this.drawContourLine(ctx, outer);

            ctx.strokeStyle = 'rgba(16, 185, 129, 0.95)';
            this.drawContourLine(ctx, inner);

            ctx.strokeStyle = 'rgba(14, 165, 233, 0.95)';
            ctx.stroke(bandPath);
            ctx.restore();
        }

        // ============================================
        // CHARM RENDERING
        // ============================================

        drawCharm(ctx, charm, nailW, nailH, options = {}) {
            let x = charm.x;
            let y = charm.y;

            if (options.isThumbInHandMode && options.shapeSet) {
                const transformed = this.transformThumbCoordinates(x, y, options.shapeSet);
                x = transformed.x;
                y = transformed.y;
            }

            const pixelX = x * nailW;
            const pixelY = y * nailH;
            const baseSize = Math.min(nailW, nailH) * 0.15;
            const size = baseSize * (charm.scale / 0.2);

            ctx.save();
            ctx.translate(pixelX, pixelY);
            ctx.rotate(charm.rotation * Math.PI / 180);

            if (charm.type.startsWith('jellyOrb')) {
                this.drawJellyOrb(ctx, size, charm.tint, charm.opacity);
            } else if (charm.type.startsWith('metalOrb')) {
                this.drawMetallicOrb(ctx, size, charm.tint, charm.metallicIntensity || 0.8);
            } else if (charm.type === 'kc3dshape1') {
                this.draw3DShape(ctx, size, charm.tint);
            }

            ctx.restore();
        }

        transformThumbCoordinates(editorX, editorY, shapeSet) {
            const params = THUMB_COORD_TRANSFORMS[shapeSet];
            if (!params) return { x: editorX, y: editorY };

            const centeredX = editorX - 0.5;
            const centeredY = editorY - 0.5;

            let handX = centeredX / params.scaleX;
            let handY = centeredY / params.scaleY;

            const perspectiveFactor = 1 + params.perspectiveStrength * (0.5 - editorY);
            handX *= perspectiveFactor;

            return {
                x: Math.max(0, Math.min(1, handX + 0.5)),
                y: Math.max(0, Math.min(1, handY + 0.5))
            };
        }

        drawJellyOrb(ctx, size, color, opacity) {
            const r = size / 2;

            ctx.save();
            ctx.globalAlpha = opacity;

            const grad = ctx.createRadialGradient(-r * 0.3, -r * 0.3, 0, 0, 0, r);
            const rgb = this.hexToRgb(color);
            grad.addColorStop(0, `rgba(${rgb.r + 60}, ${rgb.g + 60}, ${rgb.b + 60}, 0.9)`);
            grad.addColorStop(0.4, color);
            grad.addColorStop(1, `rgba(${Math.max(0, rgb.r - 40)}, ${Math.max(0, rgb.g - 40)}, ${Math.max(0, rgb.b - 40)}, 0.8)`);

            ctx.beginPath();
            ctx.arc(0, 0, r, 0, Math.PI * 2);
            ctx.fillStyle = grad;
            ctx.fill();

            ctx.beginPath();
            ctx.arc(-r * 0.35, -r * 0.35, r * 0.25, 0, Math.PI * 2);
            ctx.fillStyle = 'rgba(255, 255, 255, 0.7)';
            ctx.fill();

            ctx.beginPath();
            ctx.arc(-r * 0.15, -r * 0.5, r * 0.1, 0, Math.PI * 2);
            ctx.fillStyle = 'rgba(255, 255, 255, 0.5)';
            ctx.fill();

            ctx.restore();
        }

        drawMetallicOrb(ctx, size, color, intensity) {
            const r = size / 2;

            ctx.save();

            const grad = ctx.createRadialGradient(-r * 0.4, -r * 0.4, 0, 0, 0, r);
            grad.addColorStop(0, '#ffffff');
            grad.addColorStop(0.2, '#e0e0e0');
            grad.addColorStop(0.5, '#a0a0a0');
            grad.addColorStop(0.8, '#606060');
            grad.addColorStop(1, '#404040');

            ctx.beginPath();
            ctx.arc(0, 0, r, 0, Math.PI * 2);
            ctx.fillStyle = grad;
            ctx.fill();

            if (color !== '#c0c0c0') {
                ctx.globalCompositeOperation = 'overlay';
                ctx.globalAlpha = 0.3;
                ctx.fillStyle = color;
                ctx.fill();
                ctx.globalCompositeOperation = 'source-over';
                ctx.globalAlpha = 1;
            }

            ctx.beginPath();
            ctx.arc(-r * 0.3, -r * 0.3, r * 0.3, 0, Math.PI * 2);
            const hlGrad = ctx.createRadialGradient(-r * 0.3, -r * 0.3, 0, -r * 0.3, -r * 0.3, r * 0.3);
            hlGrad.addColorStop(0, 'rgba(255, 255, 255, 0.9)');
            hlGrad.addColorStop(1, 'rgba(255, 255, 255, 0)');
            ctx.fillStyle = hlGrad;
            ctx.fill();

            ctx.restore();
        }

        draw3DShape(ctx, size, color) {
            ctx.save();

            const scale = size / Math.max(KC_3DSHAPE_VB_W, KC_3DSHAPE_VB_H);
            ctx.scale(scale, scale);
            ctx.translate(-KC_3DSHAPE_VB_W / 2, -KC_3DSHAPE_VB_H / 2);

            const rgb = this.hexToRgb(color);
            const path1 = new Path2D(KC_3DSHAPE_PATH1);
            const path2 = new Path2D(KC_3DSHAPE_PATH2);

            // Drop shadow
            ctx.save();
            ctx.translate(4, 4);
            ctx.fillStyle = 'rgba(0, 0, 0, 0.35)';
            ctx.fill(path1);
            ctx.fill(path2);
            ctx.restore();

            // Main shape with gradient
            const grad = ctx.createLinearGradient(0, 0, KC_3DSHAPE_VB_W, KC_3DSHAPE_VB_H);
            grad.addColorStop(0, `rgb(${Math.min(255, rgb.r + 50)}, ${Math.min(255, rgb.g + 50)}, ${Math.min(255, rgb.b + 50)})`);
            grad.addColorStop(0.3, `rgb(${Math.min(255, rgb.r + 20)}, ${Math.min(255, rgb.g + 20)}, ${Math.min(255, rgb.b + 20)})`);
            grad.addColorStop(0.6, color);
            grad.addColorStop(1, `rgb(${Math.max(0, rgb.r - 40)}, ${Math.max(0, rgb.g - 40)}, ${Math.max(0, rgb.b - 40)})`);

            ctx.fillStyle = grad;
            ctx.fill(path1);
            ctx.fill(path2);

            // Highlights
            ctx.save();
            ctx.globalCompositeOperation = 'overlay';
            const hlGrad = ctx.createRadialGradient(90, 60, 5, 90, 60, 60);
            hlGrad.addColorStop(0, 'rgba(255, 255, 255, 0.6)');
            hlGrad.addColorStop(0.5, 'rgba(255, 255, 255, 0.2)');
            hlGrad.addColorStop(1, 'rgba(255, 255, 255, 0)');
            ctx.fillStyle = hlGrad;
            ctx.fill(path2);
            ctx.restore();

            ctx.strokeStyle = `rgba(${Math.max(0, rgb.r - 60)}, ${Math.max(0, rgb.g - 60)}, ${Math.max(0, rgb.b - 60)}, 0.3)`;
            ctx.lineWidth = 1;
            ctx.stroke(path1);
            ctx.stroke(path2);

            ctx.restore();
        }

        renderCharmPreview(canvas, charmId, color) {
            const ctx = canvas.getContext('2d');
            const w = canvas.width;
            const h = canvas.height;

            ctx.clearRect(0, 0, w, h);
            ctx.save();
            ctx.translate(w / 2, h / 2);

            const size = Math.min(w, h) * 0.7;

            if (charmId.startsWith('jellyOrb')) {
                this.drawJellyOrb(ctx, size, color, 0.85);
            } else if (charmId.startsWith('metalOrb')) {
                this.drawMetallicOrb(ctx, size, '#c0c0c0', 0.8);
            } else if (charmId === 'kc3dshape1') {
                this.draw3DShape(ctx, size * 1.2, color);
            }

            ctx.restore();
        }

        // ============================================
        // EDITOR HANDLERS
        // ============================================

        handleEditorMouseDown(e) {
            if (typeof e.button === 'number' && e.button !== 0) return;

            const canvas = e.currentTarget || e.target;
            if (!canvas) return;

            if (e.pointerId != null) {
                if (this.editorState.activePointerId !== null && this.editorState.activePointerId !== e.pointerId) {
                    return;
                }
                this.editorState.activePointerId = e.pointerId;
                if (typeof canvas.setPointerCapture === 'function') {
                    try {
                        canvas.setPointerCapture(e.pointerId);
                    } catch (error) {
                        // Ignore capture failures when the preview is re-rendered mid-gesture.
                    }
                }
            }

            if (e.cancelable) e.preventDefault();

            const { x, y } = this.getCanvasPoint(e, canvas);

            const design = this.state.nails[this.state.activeFinger];

            for (let i = design.charms.length - 1; i >= 0; i--) {
                const charm = design.charms[i];
                const dx = x - charm.x;
                const dy = y - charm.y;
                const dist = Math.sqrt(dx * dx + dy * dy);

                if (dist < charm.scale * 0.5) {
                    this.editorState.selectedCharm = i;
                    this.editorState.isDragging = true;
                    this.editorState.dragOffset = { x: dx, y: dy };
                    this.updateCharmList();
                    this.renderEditor();
                    return;
                }
            }

            this.editorState.selectedCharm = null;
            this.updateCharmList();
            this.renderEditor();
        }

        handleEditorMouseMove(e) {
            if (e.pointerId != null && this.editorState.activePointerId !== null && e.pointerId !== this.editorState.activePointerId) return;
            if (!this.editorState.isDragging || this.editorState.selectedCharm === null) return;

            const canvas = e.currentTarget || e.target;
            if (!canvas) return;

            if (e.cancelable) e.preventDefault();

            const { x, y } = this.getCanvasPoint(e, canvas);

            const design = this.state.nails[this.state.activeFinger];
            const charm = design.charms[this.editorState.selectedCharm];

            if (charm) {
                charm.x = Math.max(0.05, Math.min(0.95, x - this.editorState.dragOffset.x));
                charm.y = Math.max(0.05, Math.min(0.95, y - this.editorState.dragOffset.y));
                this.renderEditor();
                this.renderAllPreviews();
            }
        }

        handleEditorMouseUp(e) {
            const canvas = e && (e.currentTarget || e.target);
            if (e && e.pointerId != null && this.editorState.activePointerId !== null && e.pointerId !== this.editorState.activePointerId) {
                return;
            }

            if (canvas && e && e.pointerId != null && typeof canvas.releasePointerCapture === 'function') {
                try {
                    if (typeof canvas.hasPointerCapture !== 'function' || canvas.hasPointerCapture(e.pointerId)) {
                        canvas.releasePointerCapture(e.pointerId);
                    }
                } catch (error) {
                    // Ignore release failures for stale captures.
                }
            }

            this.editorState.isDragging = false;
            this.editorState.isResizing = false;
            this.editorState.activePointerId = null;
        }

        handleGradientDrag(e) {
            if (!this.editorState.draggingHandle) return;

            const canvas = this.container.querySelector('#editorCanvas');
            if (!canvas) return;

            const rect = canvas.getBoundingClientRect();

            const x = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
            const y = Math.max(0, Math.min(1, (e.clientY - rect.top) / rect.height));

            this.editorState.gradientHandles[this.editorState.draggingHandle] = { x, y };

            this.updateGradientHandlePositions();
            this.updateGradient();
        }

        drawSelectedCharmOutline(canvas, design) {
            if (this.editorState.selectedCharm !== null) {
                const ctx = canvas.getContext('2d');
                const charm = design.charms[this.editorState.selectedCharm];
                if (charm) {
                    const x = charm.x * canvas.width;
                    const y = charm.y * canvas.height;
                    const size = canvas.width * charm.scale * 0.4;

                    ctx.strokeStyle = '#5f11f5';
                    ctx.lineWidth = 1;
                    ctx.setLineDash([3, 3]);
                    ctx.strokeRect(x - size, y - size, size * 2, size * 2);
                    ctx.setLineDash([]);
                }
            }
        }

        async renderEditorCanvas(canvas) {
            if (!canvas) return;

            const design = this.state.nails[this.state.activeFinger];

            await this.renderNailToCanvas(canvas, this.state.activeFinger, design);
            this.drawSelectedCharmOutline(canvas, design);
        }

        async renderEditor() {
            const canvas = this.container.querySelector('#editorCanvas');
            if (!canvas) return;

            await this.renderEditorCanvas(canvas);

            this.updateGradientHandlePositions();
        }

        updateGradientHandlePositions() {
            const canvas = this.container.querySelector('#editorCanvas');
            if (!canvas) return;

            const design = this.state.nails[this.state.activeFinger];
            const editableBase = this.getEditableBaseTarget(design);
            const showGradient = !!(editableBase && editableBase.kind === 'gradient');
            const midpointToggle = this.container.querySelector('#useMidpoint');
            const showMid = showGradient && midpointToggle && midpointToggle.classList.contains('active');

            ['start', 'end', 'mid'].forEach(type => {
                const handle = this.container.querySelector(`#gradient-handle-${type}`);
                if (!handle) return;

                if (!showGradient || (type === 'mid' && !showMid)) {
                    handle.style.display = 'none';
                    return;
                }

                handle.style.display = 'block';
                const pos = this.editorState.gradientHandles[type];
                handle.style.left = `${pos.x * canvas.width}px`;
                handle.style.top = `${pos.y * canvas.height}px`;
            });
        }

        // ============================================
        // NAIL STRIP & PREVIEWS
        // ============================================

        async renderAllPreviews() {
            for (const finger of FINGERS) {
                const canvas = this.container.querySelector(`#strip${finger.charAt(0).toUpperCase() + finger.slice(1)}`);
                if (canvas) {
                    await this.renderNailToCanvas(canvas, finger, this.state.nails[finger], 'strip');
                }
            }

            await this.renderNailsOnHand();
        }

        // ============================================
        // CONTROL HANDLERS
        // ============================================

        selectHand(hand) {
            if (!this.state.hands[hand]) return;
            this.state.hand = hand;

            this.container.querySelectorAll('[data-hand]').forEach(btn => {
                btn.classList.toggle('active', btn.dataset.hand === hand);
            });

            this.container.querySelectorAll('[data-shape]').forEach(btn => {
                btn.classList.toggle('active', btn.dataset.shape === this.state.shapeSet);
            });

            this.renderHand();
            this.renderAllPreviews();
            this.selectFinger(this.state.activeFinger);
            this.updateMobileSelectorLabels();
            this.triggerDesignChange();
        }

        selectShape(shape) {
            this.state.shapeSet = shape;

            this.container.querySelectorAll('[data-shape]').forEach(btn => {
                btn.classList.toggle('active', btn.dataset.shape === shape);
            });

            this.renderHand();
            this.renderAllPreviews();
            this.renderEditor();
            this.updateMobileSelectorLabels();
            this.triggerDesignChange();
        }

        selectFinger(finger) {
            this.state.activeFinger = finger;
            this.editorState.selectedCharm = null;

            // The selected outline is now drawn into each nail canvas,
            // so we must re-render the hand overlay when active finger changes.
            this.renderNailsOnHand();

            this.container.querySelectorAll('.nail-builder__nail-strip-preview').forEach(el => {
                const parentItem = el.closest('.nail-builder__nail-strip-item');
                el.classList.toggle('active', parentItem && parentItem.dataset.finger === finger);
            });

            this.container.querySelectorAll('.nail-builder__nail-on-hand').forEach(el => {
                el.classList.toggle('active', el.dataset.finger === finger);
            });

            const fingerLabel = this.container.querySelector('#currentFingerLabel');
            if (fingerLabel) fingerLabel.textContent = finger;

            this.updateUIFromDesign();
            this.renderEditor();
            this.updateCharmList();
            this.updateMobileSelectorLabels();
        }

        syncFrenchTipControls(pattern) {
            const frenchTipControls = this.container.querySelector('#frenchTipControls');
            const tipDepthInput = this.container.querySelector('#patternTipDepth');
            const tipDepthValue = this.container.querySelector('#patternTipDepthValue');
            const smileInput = this.container.querySelector('#patternSmileCurvature');
            const smileValue = this.container.querySelector('#patternSmileCurvatureValue');

            const isFrenchTip = this.isFrenchTipPattern(pattern) && pattern.enabled;
            if (frenchTipControls) {
                frenchTipControls.style.display = isFrenchTip ? 'block' : 'none';
            }

            if (!isFrenchTip) return;

            this.ensureFrenchTipPatternFields(pattern);

            if (tipDepthInput) tipDepthInput.value = `${pattern.tipDepth}`;
            if (tipDepthValue) tipDepthValue.textContent = `${pattern.tipDepth}%`;
            if (smileInput) smileInput.value = `${pattern.smileCurvature}`;
            if (smileValue) smileValue.textContent = this.formatSignedPercent(pattern.smileCurvature);
        }

        updateUIFromDesign() {
            const design = this.state.nails[this.state.activeFinger];
            const editableBase = this.getEditableBaseTarget(design);
            const editableKind = this.getEditableBaseKind(design);

            this.container.querySelectorAll('[data-base]').forEach(btn => {
                btn.classList.toggle('active', btn.dataset.base === editableKind);
            });

            this.container.querySelectorAll('.nail-builder__base-options').forEach(el => el.style.display = 'none');
            const optionsEl = this.container.querySelector(`#${editableKind}Options`);
            if (optionsEl) optionsEl.style.display = 'block';

            if (editableKind === 'solid' && editableBase) {
                const baseColorInput = this.container.querySelector('#baseColor');
                const baseOpacityInput = this.container.querySelector('#baseOpacity');
                const baseOpacityValue = this.container.querySelector('#baseOpacityValue');
                const solidColor = this.normalizeHexColor(editableBase.color, '#f0ede8');
                if (baseColorInput) baseColorInput.value = solidColor;
                this.setActiveMobileSwatch(solidColor);
                if (baseOpacityInput) baseOpacityInput.value = `${Math.round((editableBase.opacity ?? 1) * 100)}`;
                if (baseOpacityValue) baseOpacityValue.textContent = `${Math.round((editableBase.opacity ?? 1) * 100)}%`;
            } else if (editableKind === 'nude' && editableBase) {
                const nudeOpacityInput = this.container.querySelector('#nudeOpacity');
                const nudeOpacityValue = this.container.querySelector('#nudeOpacityValue');
                if (nudeOpacityInput) nudeOpacityInput.value = `${Math.round((editableBase.opacity ?? 0.7) * 100)}`;
                if (nudeOpacityValue) nudeOpacityValue.textContent = `${Math.round((editableBase.opacity ?? 0.7) * 100)}%`;
                this.container.querySelectorAll('#nudePresets .nail-builder__color-preset').forEach(el => {
                    el.classList.toggle('active', el.dataset.nudeId === editableBase.nudeId);
                });
            } else if (editableKind === 'metallic' && editableBase) {
                const metallicIntensityInput = this.container.querySelector('#metallicIntensity');
                const metallicIntensityValue = this.container.querySelector('#metallicIntensityValue');
                if (metallicIntensityInput) metallicIntensityInput.value = `${Math.round((editableBase.intensity ?? 0.8) * 100)}`;
                if (metallicIntensityValue) metallicIntensityValue.textContent = `${Math.round((editableBase.intensity ?? 0.8) * 100)}%`;
            } else if (editableKind === 'gradient' && editableBase) {
                this.syncGradientControlsFromBase(editableBase);
            }

            const splitToggle = this.container.querySelector('#splitToggle');
            const splitOptions = this.container.querySelector('#splitOptions');
            if (splitToggle) splitToggle.classList.toggle('active', design.base.kind === 'split');
            if (splitOptions) splitOptions.style.display = design.base.kind === 'split' ? 'block' : 'none';
            if (design.base.kind === 'split') {
                const splitPositionInput = this.container.querySelector('#splitPosition');
                const splitPositionValue = this.container.querySelector('#splitPositionValue');
                const splitPercent = Math.round(this.clamp(Number(design.base.splitT ?? 0.5), 0.2, 0.8) * 100);
                if (splitPositionInput) splitPositionInput.value = `${splitPercent}`;
                if (splitPositionValue) splitPositionValue.textContent = `${splitPercent}%`;
            }

            this.container.querySelectorAll('.nail-builder__pattern-item').forEach(el => {
                const isActive = design.pattern ?
                    (el.dataset.pattern === design.pattern.id) :
                    (el.dataset.pattern === 'none');
                el.classList.toggle('active', isActive);
            });

            const patternOptions = this.container.querySelector('#patternOptions');
            if (patternOptions) {
                patternOptions.style.display = design.pattern && design.pattern.enabled ? 'block' : 'none';
            }
            if (design.pattern && design.pattern.enabled) {
                const patternTintInput = this.container.querySelector('#patternTint');
                const patternOpacityInput = this.container.querySelector('#patternOpacity');
                const patternOpacityValue = this.container.querySelector('#patternOpacityValue');
                if (patternTintInput) patternTintInput.value = design.pattern.tint || '#333333';
                if (patternOpacityInput) patternOpacityInput.value = `${Math.round((design.pattern.opacity ?? 1) * 100)}`;
                if (patternOpacityValue) patternOpacityValue.textContent = `${Math.round((design.pattern.opacity ?? 1) * 100)}%`;
            }
            this.syncFrenchTipControls(design.pattern);

            this.refreshBaseEditorDropdown();
            this.updateMobileSelectorLabels();
            this.syncDepthMeter();
        }

        refreshBaseEditorDropdown() {
            const dropdown = this.container.querySelector('#mobileDropdown');
            const content = this.container.querySelector('#mobileDropdownContent');
            if (!dropdown || !content) return;
            if (!dropdown.classList.contains('is-open')) return;
            if (dropdown.dataset.selectorType !== 'base') return;

            this.openBaseEditorDropdown(dropdown, content);
        }

        selectBaseMode(mode) {
            const design = this.state.nails[this.state.activeFinger];
            const currentEditable = this.getEditableBaseTarget(design);
            const nextBase = this.createBaseConfigByMode(mode, currentEditable);
            if (design.base.kind === 'split') {
                design.base.bottom = nextBase;
            } else {
                design.base = nextBase;
            }

            this.updateUIFromDesign();

            if (mode === 'gradient') {
                this.updateGradient();
                return;
            }

            this.renderEditor();
            this.renderAllPreviews();
            this.triggerDesignChange();
        }

        updateBaseColor(color, options = {}) {
            const design = this.state.nails[this.state.activeFinger];
            const editableBase = this.getEditableBaseTarget(design);
            if (editableBase && editableBase.kind === 'solid') {
                editableBase.color = color;
                editableBase.opacity = 1; /* always full opacity when user picks a color */
            }
            const shouldSyncMobilePicker = !!(options && options.syncMobilePicker);
            const mobileColorPicker = this.container.querySelector('#mobileColorPicker');
            if (shouldSyncMobilePicker && mobileColorPicker) {
                mobileColorPicker.value = color;
            }
            this.setActiveMobileSwatch(color);
            this.renderEditor();
            this.renderAllPreviews();
            this.triggerDesignChange();
        }

        updateBaseOpacity(value) {
            const design = this.state.nails[this.state.activeFinger];
            const editableBase = this.getEditableBaseTarget(design);
            const opacityValue = this.container.querySelector('#baseOpacityValue');
            if (opacityValue) opacityValue.textContent = `${value}%`;
            if (editableBase && editableBase.kind === 'solid') {
                editableBase.opacity = value / 100;
            }
            this.syncDepthMeter();
            this.renderEditor();
            this.renderAllPreviews();
            this.triggerDesignChange();
        }

        getDepthTarget(base) {
            if (!base || typeof base !== 'object') return null;
            if (base.kind === 'split') return this.getDepthTarget(base.bottom);
            return base;
        }

        getDepthPercent() {
            const design = this.state.nails[this.state.activeFinger];
            const target = this.getDepthTarget(design.base);
            if (!target) return 100;

            if (target.kind === 'solid' || target.kind === 'nude') {
                return Math.round((target.opacity ?? 1) * 100);
            }

            if (target.kind === 'metallic') {
                return Math.round((target.intensity ?? 0.8) * 100);
            }

            if (target.kind === 'gradient' && target.gradient && Array.isArray(target.gradient.stops)) {
                const stops = target.gradient.stops;
                if (stops.length === 0) return 100;
                const total = stops.reduce((sum, stop) => sum + (typeof stop.opacity === 'number' ? stop.opacity : 1), 0);
                return Math.round((total / stops.length) * 100);
            }

            return 100;
        }

        setDepthPercent(value) {
            const design = this.state.nails[this.state.activeFinger];
            const target = this.getDepthTarget(design.base);
            if (!target) return;

            const ratio = Math.max(0.2, Math.min(1, value / 100));

            if (target.kind === 'solid' || target.kind === 'nude') {
                target.opacity = ratio;
            } else if (target.kind === 'metallic') {
                target.intensity = ratio;
            } else if (target.kind === 'gradient' && target.gradient && Array.isArray(target.gradient.stops)) {
                target.gradient.stops = target.gradient.stops.map(stop => ({
                    ...stop,
                    opacity: ratio
                }));
            }
        }

        syncDepthMeter() {
            const slider = this.container.querySelector('#depthSlider');
            const valueEl = this.container.querySelector('#depthValue');
            const meter = this.container.querySelector('#depthMeter');
            const depthButton = this.container.querySelector('.nail-builder__depth-tool');

            const depth = this.getDepthPercent();
            if (slider) slider.value = `${depth}`;
            if (valueEl) valueEl.textContent = `${depth}%`;

            if (meter) {
                const showMeter = this.mobile.isActive
                    ? this.mobile.depthMeterOpen
                    : this.desktop.depthMeterOpen;
                meter.classList.toggle('is-open', showMeter);
            }

            if (depthButton) {
                depthButton.classList.toggle('is-active', !!meter && meter.classList.contains('is-open'));
            }
        }

        updateDepthFromMeter(value) {
            this.setDepthPercent(parseInt(value, 10));
            this.syncDepthMeter();
            this.renderEditor();
            this.renderAllPreviews();
            this.triggerDesignChange();
        }

        toggleDepthMeter() {
            if (this.mobile.isActive) {
                this.mobile.depthMeterOpen = !this.mobile.depthMeterOpen;
            } else {
                this.desktop.depthMeterOpen = !this.desktop.depthMeterOpen;
            }
            this.syncDepthMeter();
        }

        selectNudePreset(nudeId, color) {
            const design = this.state.nails[this.state.activeFinger];
            const editableBase = this.getEditableBaseTarget(design);

            this.container.querySelectorAll('#nudePresets .nail-builder__color-preset').forEach(el => {
                el.classList.toggle('active', el.dataset.nudeId === nudeId);
            });

            if (editableBase && editableBase.kind === 'nude') {
                editableBase.nudeId = nudeId;
            }
            this.renderEditor();
            this.renderAllPreviews();
            this.triggerDesignChange();
        }

        updateNudeOpacity(value) {
            const design = this.state.nails[this.state.activeFinger];
            const editableBase = this.getEditableBaseTarget(design);
            const opacityValue = this.container.querySelector('#nudeOpacityValue');
            if (opacityValue) opacityValue.textContent = `${value}%`;
            if (editableBase && editableBase.kind === 'nude') {
                editableBase.opacity = value / 100;
            }
            this.syncDepthMeter();
            this.renderEditor();
            this.renderAllPreviews();
            this.triggerDesignChange();
        }

        updateMetallicIntensity(value) {
            const design = this.state.nails[this.state.activeFinger];
            const editableBase = this.getEditableBaseTarget(design);
            const intensityValue = this.container.querySelector('#metallicIntensityValue');
            if (intensityValue) intensityValue.textContent = `${value}%`;
            if (editableBase && editableBase.kind === 'metallic') {
                editableBase.intensity = value / 100;
            }
            this.syncDepthMeter();
            this.renderEditor();
            this.renderAllPreviews();
            this.triggerDesignChange();
        }

        updateGradient() {
            const design = this.state.nails[this.state.activeFinger];
            const editableBase = this.getEditableBaseTarget(design);
            if (!editableBase || editableBase.kind !== 'gradient') return;

            const midpointToggle = this.container.querySelector('#useMidpoint');
            const useMid = midpointToggle && midpointToggle.classList.contains('active');

            const startH = this.editorState.gradientHandles.start;
            const endH = this.editorState.gradientHandles.end;
            const midH = this.editorState.gradientHandles.mid;

            const gradStartInput = this.container.querySelector('#gradStart');
            const gradMidInput = this.container.querySelector('#gradMid');
            const gradEndInput = this.container.querySelector('#gradEnd');
            const depth = this.getDepthPercent() / 100;

            const stops = [
                { t: 0, color: this.normalizeHexColor(gradStartInput ? gradStartInput.value : '', '#f0ede8'), opacity: depth }
            ];

            if (useMid) {
                const dx = endH.x - startH.x;
                const dy = endH.y - startH.y;
                const lineLengthSq = dx * dx + dy * dy;

                let midT = 0.5;
                if (lineLengthSq > 0.0001) {
                    const mx = midH.x - startH.x;
                    const my = midH.y - startH.y;
                    midT = Math.max(0.05, Math.min(0.95, (mx * dx + my * dy) / lineLengthSq));
                }

                stops.push({ t: midT, color: this.normalizeHexColor(gradMidInput ? gradMidInput.value : '', '#ff9a9e'), opacity: depth });
            }

            stops.push({ t: 1, color: this.normalizeHexColor(gradEndInput ? gradEndInput.value : '', '#fad0c4'), opacity: depth });

            editableBase.gradient = {
                x1: startH.x,
                y1: startH.y,
                x2: endH.x,
                y2: endH.y,
                stops
            };

            this.syncDepthMeter();
            this.renderEditor();
            this.renderAllPreviews();
            this.triggerDesignChange();
        }

        toggleMidpoint() {
            const toggle = this.container.querySelector('#useMidpoint');
            if (toggle) toggle.classList.toggle('active');
            this.updateGradient();
            this.refreshBaseEditorDropdown();
        }

        toggleSplit() {
            const toggle = this.container.querySelector('#splitToggle');
            const splitOptions = this.container.querySelector('#splitOptions');
            const design = this.state.nails[this.state.activeFinger];
            const enableSplit = design.base.kind !== 'split';

            if (toggle) toggle.classList.toggle('active', enableSplit);
            if (splitOptions) splitOptions.style.display = enableSplit ? 'block' : 'none';

            if (enableSplit) {
                const currentBase = JSON.parse(JSON.stringify(design.base));
                const splitPositionInput = this.container.querySelector('#splitPosition');
                design.base = {
                    kind: 'split',
                    splitT: splitPositionInput ? parseInt(splitPositionInput.value) / 100 : 0.5,
                    top: {
                        kind: 'nude',
                        nudeId: NUDE_PRESETS[0].id,
                        opacity: 0.7
                    },
                    bottom: currentBase
                };
            } else {
                if (design.base.kind === 'split') {
                    design.base = design.base.bottom && typeof design.base.bottom === 'object'
                        ? design.base.bottom
                        : this.createDefaultNailDesign().base;
                }
            }

            this.updateUIFromDesign();
            this.syncDepthMeter();
            this.renderEditor();
            this.renderAllPreviews();
            this.triggerDesignChange();
        }

        updateSplitPosition(value) {
            const design = this.state.nails[this.state.activeFinger];
            const positionValue = this.container.querySelector('#splitPositionValue');
            if (positionValue) positionValue.textContent = `${value}%`;
            const splitPositionInput = this.container.querySelector('#splitPosition');
            if (splitPositionInput) splitPositionInput.value = `${value}`;

            if (design.base.kind === 'split') {
                design.base.splitT = value / 100;
            }

            this.refreshBaseEditorDropdown();
            this.renderEditor();
            this.renderAllPreviews();
            this.triggerDesignChange();
        }

        selectPattern(patternId) {
            const design = this.state.nails[this.state.activeFinger];
            if (patternId !== 'none' && !this.getPatternDefinition(patternId)) return;

            this.container.querySelectorAll('.nail-builder__pattern-item').forEach(el => {
                el.classList.toggle('active', el.dataset.pattern === patternId);
            });

            const patternOptions = this.container.querySelector('#patternOptions');

            if (patternId === 'none') {
                design.pattern = null;
                if (patternOptions) patternOptions.style.display = 'none';
            } else {
                const patternTintInput = this.container.querySelector('#patternTint');
                const patternOpacityInput = this.container.querySelector('#patternOpacity');
                design.pattern = {
                    id: patternId,
                    tint: patternTintInput ? patternTintInput.value : '#333333',
                    opacity: patternOpacityInput ? parseInt(patternOpacityInput.value) / 100 : 1,
                    enabled: true
                };
                if (patternId === 'frenchtip') {
                    design.pattern.tipDepth = FRENCH_TIP_DEFAULT_DEPTH;
                    design.pattern.smileCurvature = FRENCH_TIP_DEFAULT_CURVATURE;
                }
                if (patternOptions) patternOptions.style.display = 'block';
            }
            this.syncFrenchTipControls(design.pattern);

            this.renderEditor();
            this.renderAllPreviews();
            this.updateMobileSelectorLabels();
            this.triggerDesignChange();
        }

        updatePatternTint(color) {
            const design = this.state.nails[this.state.activeFinger];
            if (design.pattern) {
                design.pattern.tint = color;
            }
            this.renderEditor();
            this.renderAllPreviews();
            this.triggerDesignChange();
        }

        updatePatternOpacity(value) {
            const design = this.state.nails[this.state.activeFinger];
            const opacityValue = this.container.querySelector('#patternOpacityValue');
            if (opacityValue) opacityValue.textContent = `${value}%`;
            if (design.pattern) {
                design.pattern.opacity = value / 100;
            }
            this.renderEditor();
            this.renderAllPreviews();
            this.triggerDesignChange();
        }

        updateFrenchTipDepth(value) {
            const design = this.state.nails[this.state.activeFinger];
            if (!design.pattern || !this.isFrenchTipPattern(design.pattern)) return;

            design.pattern.tipDepth = this.normalizeFrenchTipDepth(value);
            this.syncFrenchTipControls(design.pattern);
            this.renderEditor();
            this.renderAllPreviews();
            this.triggerDesignChange();
        }

        updateFrenchTipCurvature(value) {
            const design = this.state.nails[this.state.activeFinger];
            if (!design.pattern || !this.isFrenchTipPattern(design.pattern)) return;

            design.pattern.smileCurvature = this.normalizeFrenchTipCurvature(value);
            this.syncFrenchTipControls(design.pattern);
            this.renderEditor();
            this.renderAllPreviews();
            this.triggerDesignChange();
        }

        // ============================================
        // CHARM MANAGEMENT
        // ============================================

        addCharm(charmType) {
            const design = this.state.nails[this.state.activeFinger];

            if (design.charms.length >= MAX_CHARMS_PER_NAIL) {
                this.showToast(`Maximum ${MAX_CHARMS_PER_NAIL} charms per nail!`);
                return;
            }

            const charmColorInput = this.container.querySelector('#charmColor');
            const charmColor = charmColorInput ? charmColorInput.value : '#ff69b4';
            const charmTypeInfo = CHARM_TYPES.find(c => c.id === charmType);

            const newCharm = {
                id: `charm_${Date.now()}`,
                type: charmType,
                x: 0.5,
                y: 0.5,
                scale: 0.2,
                rotation: 0,
                tint: charmColor,
                opacity: charmTypeInfo && charmTypeInfo.type === 'jelly' ? 0.85 : 1,
                metallicIntensity: charmTypeInfo && charmTypeInfo.type === 'metal' ? 0.8 : undefined,
                z: design.charms.length
            };

            design.charms.push(newCharm);
            this.editorState.selectedCharm = design.charms.length - 1;

            this.updateCharmList();
            this.renderEditor();
            this.renderAllPreviews();
            this.updateMobileSelectorLabels();
            this.triggerDesignChange();
        }

        updateCharmTint(index, color) {
            const design = this.state.nails[this.state.activeFinger];
            if (design.charms[index]) {
                design.charms[index].tint = color;
            }
            this.renderEditor();
            this.renderAllPreviews();
            this.triggerDesignChange();
        }

        updateCharmSize(index, percentValue) {
            const design = this.state.nails[this.state.activeFinger];
            if (design.charms[index]) {
                const charm = design.charms[index];
                const maxScale = charm.type === 'kc3dshape1' ? MAX_CHARM_SCALE_3D : MAX_CHARM_SCALE;
                const newScale = MIN_CHARM_SCALE + (parseInt(percentValue) / 100) * (maxScale - MIN_CHARM_SCALE);
                charm.scale = Math.max(MIN_CHARM_SCALE, Math.min(maxScale, newScale));
            }
            this.renderEditor();
            this.renderAllPreviews();
            this.triggerDesignChange();
        }

        removeCharm(index) {
            const design = this.state.nails[this.state.activeFinger];
            design.charms.splice(index, 1);

            if (this.editorState.selectedCharm === index) {
                this.editorState.selectedCharm = null;
            } else if (this.editorState.selectedCharm > index) {
                this.editorState.selectedCharm--;
            }

            this.updateCharmList();
            this.renderEditor();
            this.renderAllPreviews();
            this.updateMobileSelectorLabels();
            this.triggerDesignChange();
        }

        selectCharmByIndex(index) {
            this.editorState.selectedCharm = index;
            this.updateCharmList();
            this.renderEditor();
            this.renderAllPreviews();
        }

        updateCharmList() {
            const container = this.container.querySelector('#charmList');
            if (!container) return;

            const design = this.state.nails[this.state.activeFinger];

            if (design.charms.length === 0) {
                container.innerHTML = '<div class="nail-builder__charm-list-empty">no charms added</div>';
                return;
            }

            container.innerHTML = design.charms.map((charm, idx) => {
                const type = CHARM_TYPES.find(t => t.id === charm.type);
                const isSelected = this.editorState.selectedCharm === idx;
                const maxScale = charm.type === 'kc3dshape1' ? MAX_CHARM_SCALE_3D : MAX_CHARM_SCALE;
                const sizePercent = Math.round((charm.scale - MIN_CHARM_SCALE) / (maxScale - MIN_CHARM_SCALE) * 100);

                return `
                    <div class="nail-builder__charm-list-item ${isSelected ? 'selected' : ''}" data-charm-index="${idx}">
                        <div class="nail-builder__charm-list-header">
                            <span class="nail-builder__charm-list-name">${type ? type.name.toLowerCase() : charm.type}</span>
                            <div class="nail-builder__charm-list-actions">
                                <input type="color" value="${charm.tint}" class="nail-builder__charm-list-color" data-charm-color="${idx}">
                                <button class="nail-builder__delete-btn" data-charm-delete="${idx}">×</button>
                            </div>
                        </div>
                        <div class="nail-builder__charm-size-control">
                            <label class="nail-builder__charm-size-label">size: ${sizePercent}%</label>
                            <input type="range" class="nail-builder__charm-size-slider" min="0" max="100" value="${sizePercent}" data-charm-size="${idx}">
                        </div>
                    </div>
                `;
            }).join('');

            // Add event listeners
            container.querySelectorAll('.nail-builder__charm-list-item').forEach(item => {
                const idx = parseInt(item.dataset.charmIndex);
                item.addEventListener('click', (e) => {
                    if (!e.target.matches('input, button')) {
                        this.selectCharmByIndex(idx);
                    }
                });
            });

            container.querySelectorAll('[data-charm-color]').forEach(input => {
                const idx = parseInt(input.dataset.charmColor);
                input.addEventListener('change', (e) => this.updateCharmTint(idx, e.target.value));
                input.addEventListener('click', (e) => e.stopPropagation());
            });

            container.querySelectorAll('[data-charm-delete]').forEach(btn => {
                const idx = parseInt(btn.dataset.charmDelete);
                btn.addEventListener('click', (e) => {
                    e.stopPropagation();
                    this.removeCharm(idx);
                });
            });

            container.querySelectorAll('[data-charm-size]').forEach(slider => {
                const idx = parseInt(slider.dataset.charmSize);
                slider.addEventListener('input', (e) => {
                    const label = slider.previousElementSibling;
                    if (label) label.textContent = `size: ${e.target.value}%`;
                    this.updateCharmSize(idx, e.target.value);
                });
                slider.addEventListener('click', (e) => e.stopPropagation());
            });
        }

        // ============================================
        // UTILITY FUNCTIONS
        // ============================================

        clearCurrentNail() {
            this.state.nails[this.state.activeFinger] = this.createDefaultNailDesign();
            this.editorState.selectedCharm = null;
            this.updateUIFromDesign();
            this.updateCharmList();
            this.renderEditor();
            this.renderAllPreviews();
            this.updateMobileSelectorLabels();
            this.triggerDesignChange();
        }

        applyToAllNails() {
            const currentDesign = JSON.parse(JSON.stringify(this.state.nails[this.state.activeFinger]));

            FINGERS.forEach(finger => {
                const newDesign = JSON.parse(JSON.stringify(currentDesign));
                newDesign.charms.forEach(charm => {
                    charm.id = `charm_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
                });
                this.state.nails[finger] = newDesign;
            });

            this.renderAllPreviews();
            this.showToast('Applied design to all nails!');
            this.triggerDesignChange();
        }

        hexToRgb(hex) {
            const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
            return result ? {
                r: parseInt(result[1], 16),
                g: parseInt(result[2], 16),
                b: parseInt(result[3], 16)
            } : { r: 0, g: 0, b: 0 };
        }

        getAccentColor() {
            const color = getComputedStyle(this.container).getPropertyValue('--nb-accent').trim();
            return color || '#5f11f5';
        }

        hexToRgba(hex, alpha) {
            const rgb = this.hexToRgb(hex);
            return `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, ${alpha})`;
        }

        showToast(message) {
            const toast = this.container.querySelector('#toast');
            if (toast) {
                toast.textContent = message;
                toast.classList.add('show');

                setTimeout(() => {
                    toast.classList.remove('show');
                }, 2500);
            }
        }

        // ============================================
        // EXPORT FUNCTIONS
        // ============================================

        getDesignJSON() {
            return {
                hand: this.state.hand,
                hands: this.state.hands,
                // Legacy top-level fields for backward compatibility
                shapeSet: this.state.shapeSet,
                activeFinger: this.state.activeFinger,
                nails: this.state.nails
            };
        }

        exportJSON() {
            const json = JSON.stringify(this.getDesignJSON(), null, 2);
            const jsonOutput = this.container.querySelector('#jsonOutput');
            const jsonModal = this.container.querySelector('#jsonModal');

            if (jsonOutput) jsonOutput.value = json;
            if (jsonModal) jsonModal.classList.add('active');
        }

        copyJSON() {
            const textarea = this.container.querySelector('#jsonOutput');
            if (textarea) {
                textarea.select();
                document.execCommand('copy');
                this.showToast('JSON copied to clipboard!');
            }
        }

        downloadJSON() {
            const json = JSON.stringify(this.getDesignJSON(), null, 2);
            const blob = new Blob([json], { type: 'application/json' });
            const url = URL.createObjectURL(blob);

            const a = document.createElement('a');
            a.href = url;
            a.download = `nail-design-${Date.now()}.json`;
            a.click();

            URL.revokeObjectURL(url);
            this.showToast('JSON downloaded!');
        }

        loadDesign() {
            const jsonInput = this.container.querySelector('#jsonInput');
            const loadModal = this.container.querySelector('#loadModal');

            if (jsonInput) jsonInput.value = '';
            if (loadModal) loadModal.classList.add('active');
        }

        importJSON() {
            try {
                const jsonInput = this.container.querySelector('#jsonInput');
                if (!jsonInput) return;

                const json = jsonInput.value;
                const imported = JSON.parse(json);
                if (!this.applyImportedDesign(imported)) {
                    throw new Error('Invalid design JSON');
                }

                this.closeModal('loadModal');

                this.selectHand(this.state.hand);
                this.selectShape(this.state.shapeSet);
                this.selectFinger(this.state.activeFinger);

                this.showToast('Design loaded successfully!');
            } catch (e) {
                this.showToast('Error loading design: ' + e.message);
            }
        }

        async exportPNG() {
            this.showToast('Generating PNG...');

            const exportCanvas = document.createElement('canvas');
            const exportWidth = 800;
            const exportHeight = 1600;
            exportCanvas.width = exportWidth;
            exportCanvas.height = exportHeight;

            const ctx = exportCanvas.getContext('2d');

            // Background
            ctx.fillStyle = '#1a1a2e';
            ctx.fillRect(0, 0, exportWidth, exportHeight);

            const handRect = {
                x: (exportWidth - 400) / 2,
                y: 100,
                width: 400,
                height: 750
            };
            await this.drawHandCompositionToContext(ctx, handRect, {
                hand: this.state.hand,
                shapeSet: this.state.shapeSet
            });

            // Add title
            ctx.fillStyle = '#ffffff';
            ctx.font = 'bold 24px Segoe UI, sans-serif';
            ctx.textAlign = 'center';
            ctx.fillText('KIMCAO Nail Design', exportWidth / 2, 50);

            // Add nail strip at bottom
            ctx.fillStyle = '#16213e';
            ctx.fillRect(0, exportHeight - 250, exportWidth, 250);

            const stripY = exportHeight - 200;
            const stripNailWidth = 60;
            const stripNailHeight = 120;
            const stripGap = 30;
            const totalStripWidth = (stripNailWidth + stripGap) * 5 - stripGap;
            let stripX = (exportWidth - totalStripWidth) / 2;

            for (const finger of FINGERS) {
                const nailCanvas = document.createElement('canvas');
                nailCanvas.width = stripNailWidth;
                nailCanvas.height = stripNailHeight;

                await this.renderNailToCanvas(nailCanvas, finger, this.state.nails[finger], 'strip');
                ctx.drawImage(nailCanvas, stripX, stripY);

                ctx.fillStyle = '#a0a0a0';
                ctx.font = '12px Segoe UI, sans-serif';
                ctx.textAlign = 'center';
                ctx.fillText(finger.toUpperCase(), stripX + stripNailWidth / 2, stripY + stripNailHeight + 20);

                stripX += stripNailWidth + stripGap;
            }

            // Download
            const dataUrl = exportCanvas.toDataURL('image/png');
            const a = document.createElement('a');
            a.href = dataUrl;
            a.download = `kimcao-nail-design-${Date.now()}.png`;
            a.click();

            this.showToast('PNG exported!');

            return dataUrl;
        }

        closeModal(modalId) {
            const modal = this.container.querySelector(`#${modalId}`);
            if (modal) modal.classList.remove('active');
        }

        // ============================================
        // SHOPIFY CART INTEGRATION
        // ============================================

        triggerDesignChange() {
            if (this.options.onDesignChange) {
                this.options.onDesignChange(this.getDesignJSON());
            }
        }

        async addToCart() {
            const designJSON = JSON.stringify(this.getDesignJSON());

            // Generate preview image
            const previewDataUrl = await this.exportPNG();

            if (!this.options.variantId) {
                this.showToast('No product variant selected');
                return;
            }

            const formData = {
                items: [{
                    id: this.options.variantId,
                    quantity: 1,
                    properties: {
                        '_nail_design': designJSON,
                        '_nail_design_preview': previewDataUrl.substring(0, 500) + '...',
                        'Hand': this.state.hand === 'left' ? 'Left Hand' : 'Right Hand',
                        'Shape': this.state.shapeSet.charAt(0).toUpperCase() + this.state.shapeSet.slice(1)
                    }
                }]
            };

            try {
                const response = await fetch('/cart/add.js', {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify(formData)
                });

                if (response.ok) {
                    this.showToast('Design added to cart!');

                    // Optionally redirect to cart
                    // window.location.href = '/cart';
                } else {
                    const error = await response.json();
                    this.showToast('Error: ' + (error.description || 'Could not add to cart'));
                }
            } catch (e) {
                this.showToast('Error adding to cart');
                console.error('Add to cart error:', e);
            }
        }

        // Save design to localStorage
        saveToLocalStorage() {
            const designJSON = JSON.stringify(this.getDesignJSON());
            localStorage.setItem('kimcao_nail_design', designJSON);
            this.showToast('Design saved!');
        }

        // Load design from localStorage
        loadFromLocalStorage() {
            const savedDesign = localStorage.getItem('kimcao_nail_design');
            if (savedDesign) {
                try {
                    const imported = JSON.parse(savedDesign);
                    if (!this.applyImportedDesign(imported)) return;

                    this.selectHand(this.state.hand);
                    this.selectShape(this.state.shapeSet);
                    this.selectFinger(this.state.activeFinger);

                    this.showToast('Design loaded!');
                } catch (e) {
                    console.error('Error loading saved design:', e);
                }
            }
        }
    }

    // ============================================
    // GLOBAL INITIALIZATION
    // ============================================

    // Expose to global scope for Shopify integration
    window.KimcaoNailBuilder = KimcaoNailBuilder;

})();
